// torrstream frontend (vanilla TS, bundled by esbuild for old TV browsers - see scripts/build-web.ts)
import { audioLabel, trackLang, langName } from "../src/lang";
import { isHeavy } from "../src/pick";
import { installKeys, setPlayerKeys, mediaCaps, isTV, isWebOS, prefersAtmos, canEac3, ATMOS_KEY } from "./tv";

let CAPS = mediaCaps();
// Safari / iOS play HLS natively -> full-timeline HLS from the server (+ AirPlay to Apple TV)
// (Apple only: some Android browsers claim HLS too, but can't be assumed to decode HEVC/Dolby passthrough)
// On iPhone/iPad EVERY browser (Chrome = CriOS, Firefox = FxiOS, Edge = EdgiOS) is WebKit + Apple's player: they
// play HLS like Safari and can NOT play the progressive mp4 pipe (no byte ranges) -> "Playback error". iPadOS
// Safari reports itself as a Mac: touch points tell it apart.
const IOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
// LG webOS browser: its native player decodes the progressive fMP4 pipe (sound, right video size) but never shows
// the picture - re-encoded too. A regular MP4, MSE and HLS all show it (tested per variant on the TV), so it gets
// the same server HLS as Apple (it decodes H.264/HEVC and AC-3/E-AC-3 there).
const NATIVE_HLS =
  isWebOS ||
  (!!document.createElement("video").canPlayType("application/vnd.apple.mpegurl") &&
    (IOS || (/Macintosh|AppleTV/.test(navigator.userAgent) && !/Android|Chrome|Edg|Firefox/.test(navigator.userAgent))));
const app = document.getElementById("app")!;

const esc = (s: unknown) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

const fmtSize = (b: number) => {
  b = Number(b) || 0; // a result without a size (e.g. a file-index hit) must not throw and blank the whole list
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (b >= 1024 && i < u.length - 1) (b /= 1024), i++;
  return `${b.toFixed(i > 1 ? 1 : 0)} ${u[i]}`;
};
const fmtSpeed = (b: number) => `${fmtSize(b)}/s`;
const fmtTime = (s: number) => {
  if (!isFinite(s)) return "--:--";
  s = Math.max(0, Math.floor(s));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
};

class HttpError extends Error {
  constructor(public status: number, msg: string) {
    super(msg);
  }
}

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    renderLogin();
    throw new HttpError(401, "unauthorized");
  }
  if (!res.ok) throw new HttpError(res.status, data.error ?? res.statusText);
  return data;
}

// ---------------- router ----------------
let cleanup: (() => void) | null = null;

function go(path: string, replace = false) {
  if (replace) history.replaceState(null, "", path);
  else history.pushState(null, "", path);
  route();
}

document.addEventListener("click", (e) => {
  // the logo = a fresh front page (clears the search, like the x in the search box)
  if ((e.target as HTMLElement).closest("a.brand") && !e.ctrlKey && !e.metaKey) {
    lastSearch = { q: "", cat: lastSearch.cat, results: [] };
  }
  const a = (e.target as HTMLElement).closest("a[data-link]") as HTMLAnchorElement | null;
  if (a && !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
    go(a.getAttribute("href")!);
  }
});
window.addEventListener("popstate", () => route());

// bumped on every navigation: a page that loads data only draws if it's still the page being shown
// (otherwise a slow Library answer could overwrite the Status page you clicked meanwhile)
let pageSeq = 0;
function pageGuard() {
  const mine = pageSeq;
  return () => mine === pageSeq;
}

function route() {
  pageSeq++;
  cleanup?.();
  cleanup = null;
  const p = location.pathname;
  document.querySelectorAll("nav a").forEach((a) => a.classList.toggle("active", a.getAttribute("href") === p));
  const w = p.match(/^\/watch\/([0-9a-f]{40})\/(\d+)/);
  if (w) return renderWatch(w[1]!, Number(w[2]));
  if (p === "/library") return renderLibrary();
  if (p === "/history") return renderHistory();
  if (p === "/users") return renderUsers();
  if (p === "/status") return renderStatus();
  if (p === "/apps") return renderApps();
  if (p === "/music") return renderMusic();
  if (p === "/tv") return renderTV();
  if (p === "/favs") return renderFavs();
  const pm = p.match(/^\/person\/(\d+)/);
  if (pm) return renderPerson(Number(pm[1]));
  return renderSearch();
}

// ---------------- login ----------------
function renderLogin() {
  cleanup?.();
  cleanup = null;
  setAccount(null);
  app.innerHTML = `
    <form class="login">
      <h2>Sign in</h2>
      <input type="password" name="pw" placeholder="Password" autocomplete="current-password" autocapitalize="none" autocorrect="off" spellcheck="false" autofocus />
      <button>Enter</button>
      <div class="dim" id="msg"></div>
    </form>`;
  const form = app.querySelector("form")!;
  form.onsubmit = async (e) => {
    e.preventDefault();
    const pw = (form.elements.namedItem("pw") as HTMLInputElement).value;
    const res = await fetch("/api/login", { method: "POST", body: JSON.stringify({ password: pw }) });
    const d = await res.json().catch(() => ({}));
    if (res.ok) return signedIn();
    app.querySelector("#msg")!.textContent = d.error || "Wrong password";
  };
}

async function signedIn() {
  sugCache = null;
  const me = await refreshAccount();
  if (!me.authed) {
    // the server accepted the password but the browser didn't keep the session cookie
    const msg = app.querySelector("#msg");
    if (msg) msg.textContent = "Signed in, but this browser didn't keep the login cookie (cookies blocked or a very old browser).";
    return;
  }
  route();
}

// ---------------- account (header) ----------------
let IS_ADMIN = false;
function setAccount(name: string | null, admin = false) {
  IS_ADMIN = admin;
  document.getElementById("navtv")!.hidden = !admin;
  document.getElementById("navusers")!.hidden = !admin;
  const box = document.getElementById("account")!;
  box.hidden = !name;
  document.getElementById("whoami")!.textContent = name ?? "";
  document.querySelector<HTMLElement>("nav")!.hidden = !name;
}
async function refreshAccount() {
  const me = await fetch("/api/me").then((r) => r.json()).catch(() => ({}));
  setAccount(me.authed ? me.user : null, !!me.admin);
  if (me.authed) startEvents();
  else stopEvents();
  return me;
}
document.getElementById("logout")!.onclick = async () => {
  stopEvents();
  await fetch("/api/logout", { method: "POST" }).catch(() => {});
  sugCache = null;
  history.pushState(null, "", "/");
  renderLogin();
};

// ---------------- search ----------------
/** rough guess from the release name: will it start instantly or need conversion? */
function quality(name: string) {
  const n = name.toLowerCase();
  const res = /2160p|4k|uhd/.test(n) ? "4K" : /1080p/.test(n) ? "1080p" : /720p/.test(n) ? "720p" : "";
  const heavy = isHeavy(name); // same rule the release picker uses (light releases always first)
  const atmos = /atmos/.test(n);
  // TrueHD Atmos can't be streamed as-is (it's converted and loses Atmos); DD+ Atmos can on Apple TV / TVs
  const truehd = /truehd/.test(n);
  return `${res ? `<span class="badge ${res === "4K" ? "warn" : ""}">${res}</span>` : ""}${
    heavy ? `<span class="badge warn" title="needs conversion, may take longer to start">heavy</span>` : ""
  }${
    atmos
      ? `<span class="badge atmos" title="${truehd ? "Dolby Atmos (TrueHD) - converted when streaming, Atmos is lost" : "Dolby Atmos (DD+) - kept on Apple TV / Safari and TVs with Dolby"}">Atmos${truehd ? " (TrueHD)" : ""}</span>`
      : ""
  }`;
}

let lastSearch = { q: "", cat: "all", results: [] as any[] };

/** A result's file list (fetched off the .torrent metadata, see /api/search/files), filterable by name. */
function renderFileList(panel: HTMLElement, data: { name: string; totalLength: number; files: { index: number; path: string; size: number }[] }) {
  const files = data.files;
  panel.innerHTML = `
    <input type="search" class="filesearch" placeholder="Filter ${files.length} file${files.length === 1 ? "" : "s"}\u2026" autocomplete="off" />
    <div class="filelist"></div>`;
  const input = panel.querySelector<HTMLInputElement>("input")!;
  const list = panel.querySelector<HTMLElement>(".filelist")!;
  const draw = (term: string) => {
    const t = term.trim().toLowerCase();
    const filtered = t ? files.filter((f) => f.path.toLowerCase().includes(t)) : files;
    list.innerHTML = filtered.length
      ? filtered
          .map((f) => `<div class="filerow"><span class="fpath">${esc(f.path)}</span><span class="fsize">${fmtSize(f.size)}</span></div>`)
          .join("")
      : `<div class="dim">No matching files</div>`;
  };
  draw("");
  input.oninput = () => draw(input.value);
  input.focus();
}

function renderSearch() {
  app.innerHTML = `
    <form class="searchbar">
      <input name="q" type="search" placeholder="Search movies & shows…" value="${esc(lastSearch.q)}" autofocus autocomplete="off" />
      <div class="ac" id="ac" hidden></div>
      <select name="cat">
        <option value="all">All</option>
        <option value="movies">Movies</option>
        <option value="tv">TV</option>
        <option value="boxing">Boxing</option>
      </select>
      <button>Search</button>
    </form>
    <div class="list" id="results"></div>
    <div id="suggest"></div>`;
  const form = app.querySelector("form")!;
  const catSel = form.elements.namedItem("cat") as HTMLSelectElement;
  catSel.value = lastSearch.cat;
  const out = app.querySelector("#results")!;
  const sug = app.querySelector<HTMLElement>("#suggest")!;
  const qInput = form.elements.namedItem("q") as HTMLInputElement;
  const showSuggestions = (on: boolean) => {
    sug.hidden = !on;
    if (on) renderSuggestions(sug);
  };
  const show = (results: any[]) => {
    if (!results.length) {
      out.innerHTML = lastSearch.q ? `<div class="center">No results</div>` : "";
      return;
    }
    const filesCache = new Map<number, { name: string; totalLength: number; files: { index: number; path: string; size: number }[] }>();
    out.innerHTML = results
      .map((r, i) => {
        return `
      <div class="resultitem">
      <div class="row">
        <div class="name">${esc(r.name)}${r.source === "tcb" ? `<span class="badge boxing" title="from TC-Boxing">🥊 Boxing</span>` : ""}${quality(r.name)}${r.freeleech ? `<span class="badge">free</span>` : ""}${
          r.have ? `<span class="badge">in library</span>` : ""
        }</div>
        <div class="meta hide-sm">${fmtSize(r.size)}</div>
        <div class="meta hide-sm" title="availability">${r.seeders} ●</div>
        <button class="ghost" data-files="${i}" title="look inside this torrent for a specific file">Files</button>
        <button data-i="${i}" ${r.seeders === 0 && !r.have ? "disabled" : ""}>Play</button>
      </div>
      <div class="filespanel" id="files-${i}" hidden></div>
      </div>`;
      })
      .join("");
    out.querySelectorAll("button[data-i]").forEach((b) => {
      (b as HTMLButtonElement).onclick = async () => {
        const r = results[Number((b as HTMLElement).dataset.i)];
        (b as HTMLButtonElement).disabled = true;
        b.textContent = "Starting…";
        try {
          const { hash, file } = await api("/api/open", {
            method: "POST",
            body: JSON.stringify({ id: r.id, title: r.name }),
          });
          go(`/watch/${hash}/${file}`);
        } catch (e: any) {
          b.textContent = "Failed";
          alert(e.message);
        }
      };
    });
    // "Files": peek at a result's file list (season pack / discography / box set) without opening or
    // downloading it - lazily fetched off the .torrent metadata alone, then filterable by filename
    out.querySelectorAll<HTMLButtonElement>("button[data-files]").forEach((b) => {
      b.onclick = async () => {
        const i = Number(b.dataset.files);
        const r = results[i];
        const panel = document.getElementById(`files-${i}`)!;
        if (!panel.hidden) {
          panel.hidden = true;
          return;
        }
        panel.hidden = false;
        if (panel.dataset.loaded) return;
        panel.innerHTML = `<div class="dim">Loading file list…</div>`;
        try {
          let data = filesCache.get(i);
          if (!data) {
            data = await api(`/api/search/files?id=${encodeURIComponent(r.id)}`);
            filesCache.set(i, data!);
          }
          panel.dataset.loaded = "1";
          renderFileList(panel, data!);
        } catch (e: any) {
          panel.innerHTML = `<div class="dim">Couldn't read the file list: ${esc(e.message)}</div>`;
        }
      };
    });
  };
  show(lastSearch.results);
  showSuggestions(!lastSearch.q);
  // ---- predictive search: titles while typing (Postgres catalogue on the server)
  const ac = app.querySelector<HTMLElement>("#ac")!;
  let acItems: any[] = [];
  let acSeq = 0;
  let acTimer: Timer | null = null;
  const hideAc = () => {
    ac.hidden = true;
    acItems = [];
  };
  const drawAc = () => {
    if (!acItems.length) return hideAc();
    ac.innerHTML = acItems
      .map(
        (it, i) => `
      <a class="acitem" role="button" tabindex="0" data-i="${i}">
        <span class="acthumb">${it.poster ? `<img src="${esc(it.poster.replace("/w342/", "/w92/"))}" alt="" loading="lazy">` : ""}</span>
        <span class="actext"><b>${esc(it.title)}</b>${it.year ? ` <span class="dim">(${it.year})</span>` : ""}
          <span class="actype">${it.type === "tv" ? "Series" : "Film"}</span></span>
      </a>`,
      )
      .join("");
    ac.hidden = false;
    ac.querySelectorAll<HTMLElement>(".acitem").forEach((el) => {
      const start = async () => {
        const it = acItems[Number(el.dataset.i)];
        if (!it) return;
        const q = qInput.value;
        api("/api/suggest/pick", { method: "POST", body: JSON.stringify({ q, type: it.type, tmdbId: it.tmdbId }) }).catch(() => {});
        el.classList.add("loading");
        el.querySelector(".actype")!.textContent = "Starting…";
        try {
          const { hash, file } = await api("/api/quickplay", {
            method: "POST",
            body: JSON.stringify({ type: it.type, tmdbId: it.tmdbId, caps: CAPS, alang: audioPrefs()[`${it.type}:${it.tmdbId}`] }),
          });
          go(`/watch/${hash}/${file}`);
        } catch (err: any) {
          el.classList.remove("loading");
          el.querySelector(".actype")!.textContent = err.message || "Not available";
        }
      };
      el.onclick = start;
      el.onkeydown = (e) => {
        if (e.key === "Enter") (e.preventDefault(), start());
        if (e.key === "Escape") (e.preventDefault(), hideAc(), qInput.focus());
      };
    });
  };
  const lookup = async () => {
    const q = qInput.value.trim();
    const seq = ++acSeq;
    if (q.length < 2) return hideAc();
    try {
      const items = await api<any[]>(`/api/suggest?q=${encodeURIComponent(q)}`);
      if (seq !== acSeq || qInput.value.trim() !== q) return; // a newer keystroke won
      acItems = items;
      drawAc();
    } catch {}
  };
  qInput.onkeydown = (e) => {
    if (e.key === "ArrowDown" && !ac.hidden) {
      e.preventDefault();
      ac.querySelector<HTMLElement>(".acitem")?.focus();
    }
    if (e.key === "Escape") hideAc();
  };
  // clicking elsewhere closes the list
  const onDocClick = (e: MouseEvent) => {
    if (!form.contains(e.target as Node)) hideAc();
  };
  document.addEventListener("click", onDocClick);
  cleanup = () => document.removeEventListener("click", onDocClick);

  // clearing the query brings the suggestions back
  qInput.oninput = () => {
    if (acTimer) clearTimeout(acTimer);
    acTimer = setTimeout(lookup, 150);
    if (qInput.value.trim()) return;
    hideAc();
    lastSearch = { q: "", cat: catSel.value, results: [] };
    show([]);
    showSuggestions(true);
  };
  form.onsubmit = async (e) => {
    e.preventDefault();
    const q = qInput.value;
    const cat = catSel.value;
    if (!q.trim()) {
      qInput.oninput!(new Event("input"));
      return;
    }
    showSuggestions(false);
    hideAc();
    acSeq++;
    out.innerHTML = `<div class="center"><div class="spinner" style="margin:auto"></div></div>`;
    try {
      const results = await api(`/api/search?q=${encodeURIComponent(q)}&cat=${cat}`);
      lastSearch = { q, cat, results };
      show(results);
    } catch (e: any) {
      out.innerHTML = `<div class="center">${esc(e.message)}</div>`;
    }
  };
}

// ---------------- suggestions (TMDB + personal "You might like") ----------------
let sugCache: { at: number; rows: any[]; pending: boolean } | null = null;

const posterCard = (inner: string, attrs: string, tip: string) =>
  `<div class="card" ${attrs} title="${esc(tip)}">${inner}</div>`;

function cardBody(it: { poster?: string; title: string; year?: number; vote?: number; overview?: string; sub?: string; reason?: string; inCinemas?: boolean }) {
  const meta = [it.year, it.vote ? `★ ${it.vote.toFixed(1)}` : "", it.sub].filter(Boolean).join(" · ");
  return `
    <div class="poster">
      ${it.poster ? `<img src="${esc(it.poster)}" alt="" loading="lazy" />` : `<div class="noposter">${esc(it.title)}</div>`}
      ${it.overview ? `<div class="overview">${esc(it.overview)}</div>` : ""}
      ${it.inCinemas ? `<div class="cinema" title="Only in cinemas so far - no digital release yet">In cinemas</div>` : ""}
      <div class="state"></div>
    </div>
    <div class="ctitle">${esc(it.title)}</div>
    <div class="cmeta">${esc(meta)}</div>${it.reason ? `
    <div class="creason">${esc(it.reason)}</div>` : ""}`;
}

/** which file of a library entry to resume: the TMDB-picked one if it has a position, else the furthest */
function resumeFile(it: any): [number, number] | null {
  const pos = Object.entries(it.positions ?? {})
    .map(([f, t]) => [Number(f), Number(t)] as [number, number])
    .filter(([, t]) => t > 0);
  if (!pos.length) return null;
  return pos.find(([f]) => f === it.tmdb?.file) ?? pos.sort((a, b) => b[1] - a[1])[0]!;
}

const shelf = (title: string, cards: string, cls = "") =>
  `<section class="shelf${cls ? " " + cls : ""}"><h3>${esc(title)}</h3><div class="shelf-row">${cards}</div></section>`;

/** HTML for one TMDB row; cards are bound to their items by bindShelf() */
function tmdbShelf(row: any) {
  return shelf(
    row.title,
    row.items
      .map((it: any, i: number) => {
        const tip = [`${it.title}${it.year ? ` (${it.year})` : ""}`, it.reason, it.overview].filter(Boolean).join("\n\n");
        return posterCard(cardBody(it), `role="button" tabindex="0" data-i="${i}"`, tip);
      })
      .join(""),
    row.personal ? "personal" : "",
  );
}

/** click / OK on a TMDB card -> quickplay */
function bindShelf(section: Element, row: any) {
  section.querySelectorAll<HTMLElement>(".card[data-i]").forEach((card) => {
    const it = row.items[Number(card.dataset.i)];
    if (!it) return;
    const state = card.querySelector<HTMLElement>(".state")!;
    const start = async () => {
      if (card.classList.contains("loading")) return;
      card.classList.remove("error");
      card.classList.add("loading");
      state.innerHTML = `<div class="spinner"></div><div>Starting…</div>`;
      try {
        const { hash, file } = await api("/api/quickplay", {
          method: "POST",
          body: JSON.stringify({ type: it.type, tmdbId: it.tmdbId, caps: CAPS, alang: audioPrefs()[`${it.type}:${it.tmdbId}`] }),
        });
        // still on the page with this card (front page, My favs, a person page...): open the player
        if (card.isConnected) go(`/watch/${hash}/${file}`);
      } catch (e: any) {
        card.classList.add("error");
        state.innerHTML = `<div>${esc(e.message || "Failed")}</div>`;
      } finally {
        card.classList.remove("loading");
      }
    };
    card.onclick = start;
    card.onkeydown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        start();
      }
    };
  });
}

const fetchSuggestions = () =>
  api<{ rows: any[]; pending?: boolean }>(`/api/suggestions?caps=${encodeURIComponent(CAPS)}`).then((r) => {
    sugCache = { at: Date.now(), rows: r.rows ?? [], pending: !!r.pending };
    return sugCache;
  });

function continueItems(lib: any[]) {
  return lib
    .map((it) => ({ it, r: resumeFile(it) }))
    .filter((x): x is { it: any; r: [number, number] } => !!x.r)
    .sort((a, b) => b.it.lastPlayedAt - a.it.lastPlayedAt)
    .slice(0, 20);
}

function continueHtml(cont: { it: any; r: [number, number] }[]) {
  if (!cont.length) return "";
  return shelf(
    "Continue watching",
    cont
      .map(({ it, r: [file, t] }) =>
        `<a class="card" href="/watch/${it.hash}/${file}" data-link title="${esc(it.title)}">${cardBody({
          poster: it.tmdb?.poster,
          title: it.title,
          year: it.tmdb?.year,
          sub: `resume ${fmtTime(t)}`,
        })}</a>`,
      )
      .join(""),
    "continue",
  );
}

function othersHtml(others: any[]) {
  if (!others.length) return "";
  return shelf(
    "What others are watching",
    others
      .map((it) =>
        `<a class="card" href="/watch/${it.hash}/${it.file}" data-link title="${esc(it.title)}">${cardBody({
          poster: it.poster,
          title: it.title,
          year: it.year,
          sub: `${it.users.slice(0, 2).join(", ")}${it.users.length > 2 ? ` +${it.users.length - 2}` : ""}`,
        })}</a>`,
      )
      .join(""),
    "others",
  );
}

// ---------------- live updates (Server-Sent Events <- Postgres LISTEN/NOTIFY) ----------------
let events: EventSource | null = null;
let liveTimer: Timer | null = null;

function startEvents() {
  if (events || typeof EventSource === "undefined") return;
  events = new EventSource("/api/events");
  events.addEventListener("activity", (m: MessageEvent) => {
    let a: any;
    try {
      a = JSON.parse(m.data);
    } catch {
      return;
    }
    sugCache = null;
    if (!a.self && a.kind === "watch" && a.user && a.title) toast(`${a.user} started watching ${a.title}`);
    if (liveTimer) clearTimeout(liveTimer);
    liveTimer = setTimeout(() => liveRefresh().catch(() => {}), 700);
  });
}

function stopEvents() {
  events?.close();
  events = null;
}

function toast(text: string) {
  const t = document.createElement("div");
  t.className = "toast";
  t.textContent = text;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add("gone"), 5000);
  setTimeout(() => t.remove(), 5600);
}

/** swap one shelf (by class) in place; keeps the focused card focused */
function replaceShelf(el: HTMLElement, cls: string, html: string) {
  const old = el.querySelector(`.shelf.${cls}`);
  const active = document.activeElement as HTMLElement | null;
  const focused = old && active && old.contains(active) ? active.getAttribute("href") : null;
  if (!html) {
    old?.remove();
    return;
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const sec = tmp.firstElementChild!;
  if (old) old.parentNode!.replaceChild(sec, old);
  else if (cls === "continue") el.insertBefore(sec, el.firstChild);
  else {
    const after = el.querySelector(".shelf.personal") ?? el.querySelector(".shelf.continue");
    el.insertBefore(sec, after ? after.nextSibling : el.firstChild);
  }
  if (focused) sec.querySelector<HTMLElement>(`[href="${focused}"]`)?.focus();
}

/** front page open: refresh "Continue watching" + "What others are watching" in place */
async function liveRefresh() {
  const el = document.getElementById("suggest");
  if (!el || el.hidden || !el.isConnected || location.pathname !== "/") return;
  const [lib, sug] = await Promise.all([api<any[]>("/api/library"), fetchSuggestions()]);
  if (!el.isConnected || el.hidden) return;
  replaceShelf(el, "continue", continueHtml(continueItems(lib)));
  replaceShelf(el, "others", othersHtml((sug as any).others ?? []));
}

async function renderSuggestions(el: HTMLElement) {
  // while the personal row is being generated on the server, don't hold on to a response without it
  const fresh = sugCache && Date.now() - sugCache.at < (sugCache.pending ? 15_000 : 30 * 60_000);
  const [lib, sug] = await Promise.all([
    api<any[]>("/api/library").catch(() => []),
    fresh ? sugCache! : fetchSuggestions().catch(() => ({ rows: [] as any[], pending: false })),
  ]);
  if (el.hidden) return; // user started searching meanwhile

  const cont = continueItems(lib);

  // order: Continue watching, You might like, then the TMDB rows
  const rows = sug.rows.filter((r) => r.personal).concat(sug.rows.filter((r) => !r.personal));

  let html = continueHtml(cont);
  // personal row first, then what the other users are watching, then the TMDB rows
  const personal = rows.filter((r) => r.personal);
  const rest = rows.filter((r) => !r.personal);
  html += personal.map(tmdbShelf).join("");
  html += othersHtml((sug as any).others ?? []);
  html += rest.map(tmdbShelf).join("");
  el.innerHTML = html;
  const sections = el.querySelectorAll(".shelf:not(.continue):not(.others)");
  personal.concat(rest).forEach((row, i) => sections[i] && bindShelf(sections[i]!, row));

  // first visit: the personal row is still being generated -> slot it in when it's ready,
  // without re-rendering the rest (keeps the TV remote focus where it is)
  if (sug.pending && !rows.some((r) => r.personal)) {
    let tries = 0;
    const poll = () => {
      if (!el.isConnected || el.hidden || location.pathname !== "/" || ++tries > 8) return;
      fetchSuggestions()
        .then((s) => {
          const row = s.rows.find((r) => r.personal);
          if (!el.isConnected || el.hidden || el.querySelector(".shelf.personal")) return;
          if (!row) return s.pending && setTimeout(poll, 15_000);
          const tmp = document.createElement("div");
          tmp.innerHTML = tmdbShelf(row);
          const section = tmp.firstElementChild!;
          const after = el.querySelector(".shelf.continue");
          el.insertBefore(section, after ? after.nextSibling : el.firstChild);
          bindShelf(section, row);
        })
        .catch(() => {});
    };
    setTimeout(poll, 15_000);
  }
}

// ---------------- library ----------------
async function renderLibrary() {
  const alive = pageGuard();
  app.innerHTML = `<div class="center">Loading…</div>`;
  const draw = async () => {
    const [items, all] = await Promise.all([
      api<any[]>("/api/library"),
      IS_ADMIN ? api<any[]>("/api/admin/torrents").catch(() => null) : Promise.resolve(null),
    ]);
    if (!alive()) return;
    if (!items.length && !all) {
      app.innerHTML = `<div class="center">Nothing here yet — search for something to watch.</div>`;
      return;
    }
    const adminBox = app.querySelector("#alltorrents");
    const keepScroll = adminBox ? (adminBox.querySelector(".tablewrap") as HTMLElement | null)?.scrollLeft ?? 0 : 0;
    app.innerHTML = `${items.length ? "" : `<div class="center">Nothing in the library yet.</div>`}<div class="list">${items
      .map(
        (it) => `
      <div class="row">
        <div class="name">
          <a href="/watch/${it.hash}/0" data-link data-hash="${it.hash}">${esc(it.title)}</a>
          <div class="meta">${it.addedBy ? `started by <b>${esc(it.addedBy)}</b> · ` : ""}${Math.round(it.progress * 100)}% · ${fmtSize(it.size)} · ratio <b>${it.ratio.toFixed(2)}</b> · ↑ ${fmtSize(it.uploaded)}${
            it.upspeed ? ` (${fmtSpeed(it.upspeed)})` : ""
          }${it.dlspeed ? ` · ↓ ${fmtSpeed(it.dlspeed)}` : ""}</div>
        </div>
        <div class="meta hide-sm">${esc(it.state)}</div>
        <div class="bar hide-sm"><div style="width:${it.progress * 100}%"></div></div>
        <button class="ghost" data-del="${it.hash}" title="Delete & stop seeding">✕</button>
      </div>`,
      )
      .join("")}</div>${all ? `<div id="alltorrents">${allTorrentsHtml(all)}</div>` : ""}`;
    const wrap = app.querySelector("#alltorrents .tablewrap") as HTMLElement | null;
    if (wrap) wrap.scrollLeft = keepScroll;
    app.querySelectorAll("#alltorrents [data-kind]").forEach((b) => {
      (b as HTMLElement).onclick = () => {
        torrentFilter = (b as HTMLElement).dataset.kind!;
        draw().catch(() => {});
      };
    });
    app.querySelectorAll<HTMLButtonElement>("#alltorrents [data-sort]").forEach((b) => {
      b.onclick = (e) => {
        e.stopPropagation();
        const key = b.dataset.sort!;
        const descFirst = new Set(["totalSize", "downloaded", "dlspeed", "uploaded", "upspeed", "ratio", "seeds", "addedOn", "seedingTime", "lastActivity"]);
        torrentSort = torrentSort?.key === key ? { key, dir: torrentSort.dir === 1 ? -1 : 1 } : { key, dir: descFirst.has(key) ? -1 : 1 };
        draw().catch(() => {});
      };
    });
    app.querySelectorAll("a[data-hash]").forEach((a) => {
      (a as HTMLAnchorElement).onclick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const hash = (a as HTMLElement).dataset.hash!;
        const t = await api(`/api/torrent/${hash}`);
        const vids = t.files.filter((f: any) => f.video);
        const played = Object.keys(t.positions ?? {}).map(Number);
        const f = vids.find((v: any) => played.includes(v.index)) ?? vids.sort((a: any, b: any) => b.size - a.size)[0];
        go(`/watch/${hash}/${f?.index ?? 0}`);
      };
    });
    app.querySelectorAll("button[data-del]").forEach((b) => {
      (b as HTMLButtonElement).onclick = async () => {
        if (!confirm("Delete this and stop seeding it? (hurts ratio if not seeded enough)")) return;
        await api(`/api/torrent/${(b as HTMLElement).dataset.del}`, { method: "DELETE" });
        draw();
      };
    });
  };
  const iv = setInterval(() => draw().catch(() => {}), 5000);
  cleanup = () => clearInterval(iv);
  await draw();
}

// admins: every torrent in qBittorrent (library, warm-ups, cross-seed copies)
let torrentFilter = "all";
let torrentSort: { key: string; dir: 1 | -1 } | null = null;
const KIND_LABEL: Record<string, string> = { library: "Library", warm: "Warm-up", "cross-seed": "Cross-seed", other: "Other" };
const TORRENT_SORT_KEYS: Record<string, (t: any) => number | string> = {
  name: (t) => t.name.toLowerCase(),
  kind: (t) => t.kind,
  progress: (t) => t.progress,
  totalSize: (t) => t.totalSize,
  downloaded: (t) => t.downloaded,
  dlspeed: (t) => t.dlspeed || 0,
  uploaded: (t) => t.uploaded,
  upspeed: (t) => t.upspeed || 0,
  ratio: (t) => t.ratio,
  seeds: (t) => (t.swarmSeeds >= 0 ? t.swarmSeeds : t.seeds),
  addedOn: (t) => t.addedOn,
  seedingTime: (t) => t.seedingTime,
  lastActivity: (t) => t.lastActivity,
};
function ago(sec: number) {
  if (!sec || sec < 0) return "–";
  const d = Date.now() / 1000 - sec;
  if (d < 3600) return `${Math.max(1, Math.round(d / 60))} min ago`;
  if (d < 86400) return `${Math.round(d / 3600)} h ago`;
  return `${Math.round(d / 86400)} d ago`;
}
function dur(sec: number) {
  if (!sec) return "–";
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)} h`;
  return `${(sec / 86400).toFixed(1)} d`;
}
function allTorrentsHtml(all: any[]): string {
  const kinds = ["library", "warm", "cross-seed", "other"].filter((k) => all.some((t) => t.kind === k));
  const sum = (xs: any[], f: string) => xs.reduce((n, t) => n + (t[f] || 0), 0);
  const stat = (xs: any[]) => {
    const dn = sum(xs, "downloaded");
    const up = sum(xs, "uploaded");
    return `${xs.length} · ↓ ${fmtSize(dn)} · ↑ ${fmtSize(up)} · ratio ${dn ? (up / dn).toFixed(2) : "–"}`;
  };
  const filtered = torrentFilter === "all" ? all : all.filter((t) => t.kind === torrentFilter);
  const shown = torrentSort ? [...filtered].sort((a, b) => { const f = TORRENT_SORT_KEYS[torrentSort!.key]!; const va = f(a), vb = f(b); return (va < vb ? -1 : va > vb ? 1 : 0) * torrentSort!.dir; }) : filtered;
  const chip = (k: string, label: string, xs: any[]) =>
    `<button class="chip${torrentFilter === k ? " on" : ""}" data-kind="${k}">${label} <span class="dim">${xs.length}</span></button>`;
  const arrow = (key: string) => (torrentSort?.key === key ? ` <span class="dim">${torrentSort.dir === 1 ? "▲" : "▼"}</span>` : "");
  const th = (label: string, key: string, sub?: { label: string; key: string }) =>
    `<th class="sortable" data-sort="${key}">${label}${arrow(key)}${sub ? `<div class="thspeed" data-sort="${sub.key}">${sub.label}${arrow(sub.key)}</div>` : ""}</th>`;
  const rows = shown
    .map(
      (t) => `<tr>
        <td class="tname"><div title="${esc(t.name)}">${esc(t.name)}</div>${
          t.forTitle && t.kind !== "library" ? `<div class="dim">for ${esc(t.forTitle)}</div>` : t.addedBy ? `<div class="dim">by ${esc(t.addedBy)}</div>` : ""
        }</td>
        <td><span class="badge k-${t.kind}">${KIND_LABEL[t.kind] ?? t.kind}</span><div class="dim">${esc(t.tracker || "")}</div></td>
        <td>${esc(t.state)}<div class="dim">${Math.round(t.progress * 100)}%${t.size !== t.totalSize ? ` of ${fmtSize(t.size)}` : ""}</div></td>
        <td class="num">${fmtSize(t.totalSize)}</td>
        <td class="num">${fmtSize(t.downloaded)}</td>
        <td class="num">${t.dlspeed ? fmtSpeed(t.dlspeed) : "–"}</td>
        <td class="num">${fmtSize(t.uploaded)}</td>
        <td class="num">${t.upspeed ? fmtSpeed(t.upspeed) : "–"}</td>
        <td class="num">${t.ratio.toFixed(2)}</td>
        <td class="num">${t.seeds}/${t.peers}<div class="dim">swarm ${t.swarmSeeds >= 0 ? t.swarmSeeds : "?"}/${t.swarmPeers >= 0 ? t.swarmPeers : "?"}</div></td>
        <td>${ago(t.addedOn)}<div class="dim">seeded ${dur(t.seedingTime)}</div></td>
        <td>${ago(t.lastActivity)}</td>
      </tr>`,
    )
    .join("");
  return `
    <h3 class="alltitle">All torrents <span class="dim">(admins only)</span></h3>
    <div class="dim">${stat(all)}</div>
    <div class="chips">${chip("all", "All", all)}${kinds.map((k) => chip(k, KIND_LABEL[k] ?? k, all.filter((t) => t.kind === k))).join("")}</div>
    ${torrentFilter !== "all" ? `<div class="dim">${KIND_LABEL[torrentFilter] ?? torrentFilter}: ${stat(shown)}</div>` : ""}
    <div class="tablewrap"><table class="torrents">
      <tr>${th("Torrent", "name")}${th("Kind", "kind")}${th("State", "progress")}${th("Size", "totalSize")}${th("Down", "downloaded")}${th("↓ speed", "dlspeed")}${th("Up", "uploaded")}${th("↑ speed", "upspeed")}${th("Ratio", "ratio")}${th("Seeds/peers", "seeds")}${th("Added", "addedOn", { label: "seeded", key: "seedingTime" })}${th("Last active", "lastActivity")}</tr>
      ${rows}
    </table></div>`;
}

// ---------------- watch ----------------
// ---------------- history ----------------
async function renderHistory() {
  const alive = pageGuard();
  app.innerHTML = `<div class="center">Loading…</div>`;
  const draw = async () => {
    const items = await api<any[]>("/api/history");
    if (!alive()) return;
    if (!items.length) {
      app.innerHTML = `<div class="center">Nothing watched yet.</div>`;
      return;
    }
    const day = (t: number) => new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    app.innerHTML = `<h2>History</h2><div class="list">${items
      .map(
        (it) => `
      <div class="row hist">
        <div class="hthumb">${it.poster ? `<img src="${esc(it.poster)}" alt="" loading="lazy">` : ""}</div>
        <div class="name">
          <a href="/watch/${it.hash}/${it.file}" data-link>${esc(it.title)}</a>${it.year ? ` <span class="dim">(${it.year})</span>` : ""}
          <div class="meta">${day(it.lastPlayedAt)}${
            it.watchedFiles > 1 ? ` · ${it.watchedFiles} episodes` : ""
          }${it.completed ? ` · ${it.completed > 1 ? it.completed + " finished" : "finished"}` : it.position ? ` · stopped at ${fmtTime(it.position)}` : ""}</div>
        </div>
        <button class="ghost heart ${it.rating > 0 ? "on" : ""}" data-heart="${it.hash}" title="${it.rating > 0 ? "Remove from My favs" : "Add to My favs (shows: all seasons are downloaded)"}">${it.rating > 0 ? "♥" : "♡"}</button>
        <a class="button" href="/watch/${it.hash}/${it.file}" data-link>${it.position ? "Resume" : "Play"}</a>
        <button class="ghost" data-del="${it.hash}" title="Remove from history">✕</button>
      </div>`,
      )
      .join("")}</div>`;
    app.querySelectorAll<HTMLButtonElement>("button[data-heart]").forEach((b) => {
      b.onclick = async () => {
        const it = items.find((x) => x.hash === b.dataset.heart)!;
        const next = it.rating > 0 ? 0 : 1;
        await api("/api/rating", { method: "POST", body: JSON.stringify({ hash: it.hash, rating: next }) }).catch(() => {});
        sugCache = null;
        draw();
      };
    });
    app.querySelectorAll<HTMLButtonElement>("button[data-del]").forEach((b) => {
      b.onclick = async () => {
        await api(`/api/history/${b.dataset.del}`, { method: "DELETE" });
        sugCache = null;
        draw();
      };
    });
  };
  await draw();
}

// ---------------- status (all users) ----------------
// Grafana's frontend needs a modern browser (not Tizen's old Chromium): feature-test before embedding
const modernBrowser = typeof (window as any).structuredClone === "function" && !isTV;

// ---------------- MUSiC: browser + Subsonic apps + API keys ----------------
type MusicView =
  | { kind: "artists" }
  | { kind: "artist"; id: string }
  | { kind: "album"; id: string }
  | { kind: "playlist"; id: string };
type MusicTrack = { id: string; title: string; artist?: string; album?: string; albumId?: string; duration?: number };
type PlaylistSummary = { id: string; name: string; trackCount: number; special?: boolean };

async function renderMusic() {
  const alive = pageGuard();
  // the Last.fm auth callback redirects back here with ?lastfm=linked|error - show it once, then clean the URL
  const lfParam = new URLSearchParams(location.search).get("lastfm");
  if (lfParam === "linked" || lfParam === "error") {
    history.replaceState(null, "", location.pathname);
    toast(lfParam === "linked" ? "Last.fm account linked." : "Could not link Last.fm account.");
  }
  app.innerHTML = `
    <div class="music">
      <h2>MUSiC</h2>
      <div id="musadmin"></div>
      <div id="muslastfm"></div>
      <div id="musreco"></div>
      <div class="musicshell">
        <nav class="mussidebar">
          <button class="mussidebtn" data-nav="artists" id="musartistsnav">Artists</button>
          <div class="mussidelibhead"><span>Your library</span><span class="mussidelibbtns"><button class="ghost round" id="musaiplaylist" title="Generate a playlist with AI" aria-label="Generate a playlist with AI">\u2728</button><button class="ghost round" id="musnewplaylist" title="New playlist" aria-label="New playlist">+</button></span></div>
          <div id="mussidelib" class="mussidelist"></div>
        </nav>
        <div class="musmain">
          <div class="musicsearch">
            <input id="musq" type="search" placeholder="Artists, albums, tracks — in your library or the download index…" autocomplete="off" />
          </div>
          <div id="musearch" class="musicsearchresults" hidden>
            <div id="muslibresults"></div>
            <div id="mustrackersection" hidden>
              <h3>Ready to download</h3>
              <div class="list" id="mustrackerresults"></div>
            </div>
          </div>
          <div id="musbrowse"></div>
          <h3>Connect with other apps</h3>
          <div id="musconnect"></div>
          <h3>API keys</h3>
          <div id="muskeys"></div>
        </div>
      </div>
    </div>
    <div class="playerbar" id="musplayer" hidden>
      <audio id="musaudio" preload="metadata"></audio>
      <div class="musplayinfo">
        <div class="musplaythumb" id="musplaythumb"></div>
        <div class="musplaytext">
          <div class="mustitle" id="mustitle"></div>
          <div class="musartist" id="musartistname"></div>
        </div>
        <button class="ghost round heart" id="musplayheart" title="Like" aria-label="Like" hidden>\u2661</button>
      </div>
      <div class="musplaycenter">
        <div class="mustransport">
          <button class="ghost round" id="musprev" title="Previous">⏮</button>
          <button class="round big" id="musplay" title="Play / pause">▶</button>
          <button class="ghost round" id="musnext" title="Next">⏭</button>
        </div>
        <div class="musseekrow">
          <span class="mustime" id="muscur">0:00</span>
          <input type="range" id="musseek" min="0" max="1000" value="0" />
          <span class="mustime" id="musdur">--:--</span>
        </div>
      </div>
      <button class="ghost round" id="musshuffle" title="Shuffle: play 50 random tracks">🔀</button>
    </div>`;
  const $ = <T extends HTMLElement>(id: string) => app.querySelector<T>("#" + id)!;
  const fmtDate = (t?: number) => (t ? new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

  // ---- player (persists across browse-view changes; torn down only when leaving /music) ----
  const audioEl = $<HTMLAudioElement>("musaudio");
  const playerBar = $("musplayer");
  const playBtn = $<HTMLButtonElement>("musplay");
  const titleEl = $("mustitle");
  const artistEl = $("musartistname");
  const curEl = $("muscur");
  const durEl = $("musdur");
  const seekEl = $<HTMLInputElement>("musseek");
  const prevBtn = $<HTMLButtonElement>("musprev");
  const nextBtn = $<HTMLButtonElement>("musnext");
  const shuffleBtn = $<HTMLButtonElement>("musshuffle");

  let queue: MusicTrack[] = [];
  let qIdx = -1;
  let seeking = false;

  // ---- scrobbling (local play-counts + Last.fm, server-side) - fires regardless of Last.fm link state ----
  let curTrackId: string | null = null;
  let curTrackStartedAt = 0;
  let scrobbleSubmitted = false;
  const pingScrobble = (id: string, submission: boolean, startedAt?: number) => {
    api("/api/music/scrobble", { method: "POST", body: JSON.stringify({ id, submission, startedAt }) }).catch(() => {});
  };

  const showPlayer = (on: boolean) => {
    playerBar.hidden = !on;
    document.body.classList.toggle("hasplayer", on);
  };
  const thumbEl = $("musplaythumb");
  const playHeartBtn = $<HTMLButtonElement>("musplayheart");
  const loadTrack = (i: number) => {
    const t = queue[i];
    if (!t) return;
    qIdx = i;
    curTrackId = t.id;
    curTrackStartedAt = Date.now();
    scrobbleSubmitted = false;
    audioEl.src = `/media/music/${encodeURIComponent(t.id)}/raw`;
    titleEl.textContent = t.title;
    artistEl.textContent = [t.artist, t.album].filter(Boolean).join(" — ");
    thumbEl.classList.toggle("noart", !t.albumId);
    thumbEl.style.backgroundImage = t.albumId ? `url(/media/music/${encodeURIComponent(t.albumId)}/cover)` : "";
    playHeartBtn.hidden = false;
    playHeartBtn.dataset.heart = t.id;
    setHeart(t.id, starredTracks.has(t.id));
    showPlayer(true);
    audioEl.play().catch(() => {});
    pingScrobble(t.id, false); // "now playing"
  };
  const playQueue = (tracks: MusicTrack[], startIdx: number) => {
    queue = tracks;
    loadTrack(startIdx);
  };
  const playNext = () => {
    if (qIdx + 1 < queue.length) loadTrack(qIdx + 1);
    else audioEl.pause();
  };
  const playPrev = () => {
    if (audioEl.currentTime > 3) {
      audioEl.currentTime = 0;
      return;
    }
    if (qIdx > 0) loadTrack(qIdx - 1);
    else audioEl.currentTime = 0;
  };
  playBtn.onclick = () => (audioEl.paused ? audioEl.play().catch(() => {}) : audioEl.pause());
  nextBtn.onclick = playNext;
  prevBtn.onclick = playPrev;
  shuffleBtn.onclick = async () => {
    try {
      const tracks = await api<MusicTrack[]>("/api/music/random?n=50");
      if (!alive()) return;
      playQueue(tracks, 0);
    } catch {}
  };
  audioEl.addEventListener("play", () => (playBtn.textContent = "⏸"));
  audioEl.addEventListener("pause", () => (playBtn.textContent = "▶"));
  audioEl.addEventListener("ended", () => playNext());
  audioEl.addEventListener("loadedmetadata", () => (durEl.textContent = fmtTime(audioEl.duration)));
  audioEl.addEventListener("timeupdate", () => {
    curEl.textContent = fmtTime(audioEl.currentTime);
    if (!seeking && isFinite(audioEl.duration) && audioEl.duration > 0) {
      seekEl.value = String(Math.round((audioEl.currentTime / audioEl.duration) * 1000));
    }
    // Last.fm scrobble rule: played >= min(duration/2, 4min), and the track itself is longer than 30s
    if (!scrobbleSubmitted && curTrackId && isFinite(audioEl.duration) && audioEl.duration > 30) {
      if (audioEl.currentTime >= Math.min(audioEl.duration / 2, 240)) {
        scrobbleSubmitted = true;
        pingScrobble(curTrackId, true, curTrackStartedAt);
      }
    }
  });
  seekEl.addEventListener("input", () => (seeking = true));
  seekEl.addEventListener("change", () => {
    seeking = false;
    if (isFinite(audioEl.duration) && audioEl.duration > 0) audioEl.currentTime = (Number(seekEl.value) / 1000) * audioEl.duration;
  });

  // ---- admin-only rescan ----
  let scanPollTimer: Timer | null = null;
  const drawAdmin = async () => {
    const box = $("musadmin");
    if (!IS_ADMIN) {
      box.innerHTML = "";
      return;
    }
    box.innerHTML = `<div class="row"><button id="musrescan">Rescan library</button><div class="meta" id="musscanmsg">Loading…</div></div>`;
    const msg = box.querySelector("#musscanmsg")!;
    const btn = box.querySelector<HTMLButtonElement>("#musrescan")!;
    const drawStatus = async () => {
      const s = await api<any>("/api/music/scanstatus").catch(() => null);
      if (!alive() || !box.isConnected) return;
      if (!s) {
        msg.textContent = "";
        return;
      }
      if (s.scanning) {
        msg.textContent = "Scanning…";
        if (!scanPollTimer) scanPollTimer = setInterval(() => drawStatus().catch(() => {}), 2000);
      } else {
        if (scanPollTimer) {
          clearInterval(scanPollTimer);
          scanPollTimer = null;
        }
        const c = s.counts ?? {};
        msg.textContent = `${s.lastScanAt ? `Last scanned ${agoMs(s.lastScanAt)}` : "Never scanned yet"} · ${(c.tracks ?? 0).toLocaleString()} tracks · ${(c.albums ?? 0).toLocaleString()} albums · ${(c.artists ?? 0).toLocaleString()} artists`;
      }
    };
    btn.onclick = async () => {
      btn.disabled = true;
      try {
        await api("/api/music/rescan", { method: "POST" });
      } catch (e: any) {
        msg.textContent = e.message;
      }
      btn.disabled = false;
      drawStatus();
    };
    await drawStatus();
  };

  // ---- connect / compatibility ----
  const drawConnect = () => {
    const user = document.getElementById("whoami")!.textContent || "your name";
    const untested = `<span class="badge">not tested yet</span>`;
    const row = (appName: string, where: string, state: string, note: string) =>
      `<tr><td><b>${appName}</b><div class="dim">${where}</div></td><td>${state}</td><td>${note}</td></tr>`;
    const table = (rows: string) => `<table class="apps"><tr><th>App</th><th>Status</th><th>Notes</th></tr>${rows}</table>`;
    $("musconnect").innerHTML = `
      <div class="appsinfo">
        <p>This server also speaks the <b>OpenSubsonic</b> API, so you can browse and stream this music library with
        Subsonic-compatible apps on phones, tablets and desktops.</p>
        <div class="connect">
          <div><span class="dim">Server address</span><code>${esc(location.origin)}</code></div>
          <div><span class="dim">User name</span><code>${esc(user)}</code></div>
          <div><span class="dim">Password</span><span>create an API key below and use it as the password instead -
          recommended over your account password in these apps' login fields (they connect via <code>/rest</code>).</span></div>
        </div>
        ${table(
          row("Symfonium", "Android, paid", untested, "") +
            row("Feishin", "desktop / web, free", untested, "") +
            row("Substreamer", "Android, free", untested, "") +
            row("DSub", "Android, free", untested, "") +
            row("Submusic / Supersonic", "desktop, free", untested, "") +
            row("Amperfy", "iOS, free", untested, "") +
            row("play:Sub", "iOS, paid", untested, ""),
        )}
      </div>`;
  };

  // ---- Last.fm: link / unlink ----
  // Last.fm has no shared server-wide key - every user registers their own (free) API app and pastes its
  // key/secret in below (see src/music/lastfm.ts's module doc comment), so this section always renders
  // (unlike other optional/API-key-gated panes here that disappear entirely when unconfigured - this one
  // is never "unconfigured" for everyone, only for whoever hasn't set their own key yet).
  const drawLastfm = async () => {
    const box = $("muslastfm");
    let status: { hasApiKey: boolean; linked: boolean; username?: string };
    try {
      status = await api("/api/music/lastfm/status");
    } catch {
      if (alive()) box.innerHTML = "";
      return;
    }
    if (!alive() || !box.isConnected) return;
    box.innerHTML = `
      <h3>Last.fm</h3>
      <div class="musickeybox">
        <p class="dim">Each person uses their own (free) Last.fm API app - there's no shared one. Register at
        <a href="https://www.last.fm/api/account/create" target="_blank" rel="noopener">last.fm/api/account/create</a>
        and paste its key + shared secret below. Enables scrobbling what you play here, library
        recommendations from your Last.fm listening, and lets the tracker search below find a song even when
        it's just a track inside an album (e.g. "shook ones").</p>
        <div id="muslastfmkeystatus"></div>
        <form class="searchbar" id="muslastfmkeyform" ${status.hasApiKey ? "hidden" : ""}>
          <input name="apiKey" placeholder="API key" autocomplete="off" />
          <input name="apiSecret" placeholder="Shared secret" autocomplete="off" />
          <button>Save</button>
        </form>
        <div id="muslastfmconnectbox"></div>
      </div>`;
    const keyStatus = box.querySelector<HTMLElement>("#muslastfmkeystatus")!;
    const keyForm = box.querySelector<HTMLFormElement>("#muslastfmkeyform")!;
    const connectBox = box.querySelector<HTMLElement>("#muslastfmconnectbox")!;

    keyStatus.innerHTML = status.hasApiKey
      ? `<div>API key: <b>configured</b> <button class="ghost" id="muslastfmchangekey">Change</button> <button class="ghost" id="muslastfmremovekey">Remove</button></div>`
      : "";
    if (status.hasApiKey) {
      keyStatus.querySelector<HTMLButtonElement>("#muslastfmchangekey")!.onclick = () => {
        keyForm.hidden = false;
        keyStatus.hidden = true;
      };
      keyStatus.querySelector<HTMLButtonElement>("#muslastfmremovekey")!.onclick = async (e) => {
        const btn = e.currentTarget as HTMLButtonElement;
        if (!confirm("Remove your Last.fm API key? This also disconnects your linked account (if any) - scrobbling and recommendations from Last.fm stop until you set it up again.")) return;
        btn.disabled = true;
        await api("/api/music/lastfm/apikey", { method: "DELETE" }).catch(() => {});
        drawLastfm().catch(() => {});
      };
    }
    keyForm.onsubmit = async (e) => {
      e.preventDefault();
      const apiKey = (keyForm.elements.namedItem("apiKey") as HTMLInputElement).value.trim();
      const apiSecret = (keyForm.elements.namedItem("apiSecret") as HTMLInputElement).value.trim();
      if (!apiKey || !apiSecret) return;
      const btn = keyForm.querySelector("button")!;
      btn.disabled = true;
      try {
        await api("/api/music/lastfm/apikey", { method: "POST", body: JSON.stringify({ apiKey, apiSecret }) });
        drawLastfm().catch(() => {});
      } catch (e: any) {
        alert(e.message || "Could not save the Last.fm API key.");
        btn.disabled = false;
      }
    };

    if (!status.hasApiKey) {
      connectBox.innerHTML = "";
    } else if (status.linked) {
      connectBox.innerHTML = `
        <div>Linked as <b>${esc(status.username ?? "")}</b></div>
        <div class="dim">torrstream can scrobble what you play here on your behalf until you unlink.</div>
        <button class="ghost" id="muslastfmunlink">Unlink</button>`;
      const btn = connectBox.querySelector<HTMLButtonElement>("#muslastfmunlink")!;
      btn.onclick = async () => {
        btn.disabled = true;
        await api("/api/music/lastfm/unlink", { method: "POST" }).catch(() => {});
        drawLastfm().catch(() => {});
      };
    } else {
      connectBox.innerHTML = `<button id="muslastfmconnect">Connect Last.fm</button>`;
      const btn = connectBox.querySelector<HTMLButtonElement>("#muslastfmconnect")!;
      btn.onclick = async () => {
        btn.disabled = true;
        try {
          const { url } = await api<{ url: string }>("/api/music/lastfm/auth-url");
          location.href = url; // needs a real top-level navigation, last.fm's auth page refuses to be framed
        } catch (e: any) {
          alert(e.message || "Could not start Last.fm sign-in.");
          btn.disabled = false;
        }
      };
    }
  };

  // ---- "Recommended for you": in-library artists matched from Last.fm / local listening ----
  const drawReco = async () => {
    const box = $("musreco");
    let r: { recommendations: { id: string; name: string; because: string[] }[]; reason?: string };
    try {
      r = await api("/api/music/recommendations");
    } catch {
      if (alive()) box.innerHTML = "";
      return;
    }
    if (!alive() || !box.isConnected) return;
    const recs = r.recommendations ?? [];
    if (!recs.length) {
      // "not enough data yet" (no local plays and no linked account) gets a friendly note; a plain empty
      // list (seeds existed, just no matches) stays silent, same as other optional rows elsewhere
      box.innerHTML = r.reason
        ? `<div class="center dim">Play some music here (or link Last.fm above) to get recommendations from your library.</div>`
        : "";
      return;
    }
    const cards = recs
      .map((rec) => {
        const because = rec.because.length ? `because you like ${rec.because.join(", ")}` : "";
        return posterCard(
          `<div class="poster"><div class="noposter">${esc(rec.name)}</div></div>
           <div class="ctitle">${esc(rec.name)}</div>
           ${because ? `<div class="creason">${esc(because)}</div>` : ""}`,
          `role="button" tabindex="0" data-reco-artist="${esc(rec.id)}"`,
          because,
        );
      })
      .join("");
    box.innerHTML = shelf("Recommended for you", cards);
    box.querySelectorAll<HTMLElement>("[data-reco-artist]").forEach((el) => {
      const openReco = () => openArtist(el.dataset.recoArtist!);
      el.onclick = openReco;
      el.onkeydown = (e) => e.key === "Enter" && openReco();
    });
  };

  // ---- API keys ----
  const initKeys = async () => {
    const box = $("muskeys");
    box.innerHTML = `
      <div id="muskeylist"><div class="dim">Loading…</div></div>
      <form class="searchbar" id="muskeyform">
        <input name="label" placeholder="Label (e.g. “phone”)" autocomplete="off" />
        <button>Create key</button>
      </form>
      <div id="muskeynew"></div>`;
    const refreshList = async () => {
      const listBox = box.querySelector("#muskeylist");
      if (!listBox) return;
      let keys: any[];
      try {
        keys = await api<any[]>("/api/music/apikeys");
      } catch (e: any) {
        if (alive()) listBox.innerHTML = `<div class="dim">${esc(e.message)}</div>`;
        return;
      }
      if (!alive() || !listBox.isConnected) return;
      listBox.innerHTML = keys.length
        ? `<div class="list">${keys
            .map(
              (k) => `
        <div class="row">
          <div class="name"><b>${esc(k.label || "(unlabeled)")}</b>
            <div class="meta">created ${fmtDate(k.createdAt)} · ${k.lastUsedAt ? `last used ${agoMs(k.lastUsedAt)}` : "never used"}</div>
          </div>
          <button class="ghost" data-revoke="${esc(k.id)}" data-label="${esc(k.label || "")}">Revoke</button>
        </div>`,
            )
            .join("")}</div>`
        : `<div class="dim">No API keys yet.</div>`;
      listBox.querySelectorAll<HTMLButtonElement>("button[data-revoke]").forEach((b) => {
        b.onclick = async () => {
          if (!confirm(`Revoke the API key "${b.dataset.label || "unlabeled"}"? Apps using it will stop working.`)) return;
          await api(`/api/music/apikeys/${encodeURIComponent(b.dataset.revoke!)}`, { method: "DELETE" }).catch(() => {});
          refreshList();
        };
      });
    };
    const form = box.querySelector<HTMLFormElement>("#muskeyform")!;
    form.onsubmit = async (e) => {
      e.preventDefault();
      const input = form.elements.namedItem("label") as HTMLInputElement;
      const label = input.value.trim();
      input.value = "";
      try {
        const { key } = await api<{ id: string; key: string }>("/api/music/apikeys", {
          method: "POST",
          body: JSON.stringify({ label: label || undefined }),
        });
        const newBox = box.querySelector("#muskeynew")!;
        newBox.innerHTML = `
          <div class="musickeybox">
            <div class="dim">New API key — copy it now, it won't be shown again:</div>
            <code>${esc(key)}</code>
            <button class="ghost" id="muscopykey">Copy</button>
          </div>`;
        newBox.querySelector<HTMLButtonElement>("#muscopykey")!.onclick = () => {
          try {
            navigator.clipboard?.writeText(key).catch(() => {});
          } catch {}
        };
        refreshList();
      } catch (e: any) {
        alert(e.message);
      }
    };
    await refreshList();
  };

  // ---- browse: artists -> artist -> album ----
  let view: MusicView = { kind: "artists" };
  const browseBox = $("musbrowse");

  const bindArt = () => {
    browseBox.querySelectorAll<HTMLImageElement>(".musiccard .poster img, .musicalbumart img, .musicartistart img").forEach((img) => {
      img.onerror = () => {
        img.closest(".poster")?.classList.add("noart");
        img.remove();
      };
    });
  };

  /** fetch an album's tracks and start playing it right away - the hover "play" button on album cards
   *  (Spotify-style: play without having to open the album first). */
  const playAlbumById = async (id: string) => {
    try {
      const al = await api<any>(`/api/music/album/${encodeURIComponent(id)}`);
      const tracks = (al.tracks ?? []).map((t: any) => ({ id: t.id, title: t.title, artist: al.artist, album: al.name, albumId: al.id, duration: t.duration }));
      if (tracks.length) playQueue(tracks, 0);
    } catch {}
  };
  const bindCardPlay = () => {
    browseBox.querySelectorAll<HTMLButtonElement>("[data-playalbum]").forEach((btn) => {
      const go = (e: Event) => {
        e.stopPropagation();
        playAlbumById(btn.dataset.playalbum!);
      };
      btn.onclick = go;
      btn.onkeydown = (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), go(e));
    });
  };

  // ---- "heart" a track + playlists, Spotify-style: liking a song is really just adding it to - and the
  // heart's filled/outline state is really just membership in - a pinned, un-deletable "Liked Songs"
  // playlist (server.ts special-cases playlist id "liked" onto music/activity.ts's existing starred-
  // tracks, the same data an external Subsonic client's own "star" already uses - one heart toggle,
  // whichever app did it). Bound once, delegated on `app` (not re-bound per render) so it keeps working
  // across every view that shows track rows - album, playlist, and library search results - without each
  // one needing its own rebinding code; `trackActionsHtml` is the one snippet all three embed.
  let starredTracks = new Set<string>();
  let playlistsCache: PlaylistSummary[] | null = null;
  const loadPlaylists = async (force = false) => {
    if (playlistsCache && !force) return playlistsCache;
    playlistsCache = await api<PlaylistSummary[]>("/api/music/playlists");
    return playlistsCache;
  };
  const setHeart = (id: string, on: boolean) => {
    if (on) starredTracks.add(id);
    else starredTracks.delete(id);
    app.querySelectorAll<HTMLButtonElement>(`[data-heart="${CSS.escape(id)}"]`).forEach((b) => {
      b.classList.toggle("on", on);
      b.textContent = on ? "\u2665" : "\u2661";
      b.title = on ? "Remove from Liked Songs" : "Like";
    });
  };
  const trackActionsHtml = (id: string) => `
    <button class="ghost round heart rowaction${starredTracks.has(id) ? " on" : ""}" data-heart="${esc(id)}" title="${starredTracks.has(id) ? "Remove from Liked Songs" : "Like"}" aria-label="Like">${starredTracks.has(id) ? "\u2665" : "\u2661"}</button>
    <div class="addwrap"><button class="ghost round rowaction" data-addmenu="${esc(id)}" title="Add to playlist" aria-label="Add to playlist">+</button><div class="addmenu" hidden></div></div>`;
  const toggleHeart = async (id: string) => {
    const was = starredTracks.has(id);
    setHeart(id, !was); // optimistic - trackers/db round-trip shouldn't make a heart click feel laggy
    try {
      await api("/api/music/star", { method: was ? "DELETE" : "POST", body: JSON.stringify({ id }) });
      // unliking while looking at Liked Songs itself: it just fell out of this exact list
      if (was && view.kind === "playlist" && view.id === "liked") browseBox.querySelector(`[data-trackid="${CSS.escape(id)}"]`)?.remove();
    } catch (e: any) {
      setHeart(id, was); // revert on failure
      toast(e.message || "Couldn't update Liked Songs");
    }
  };
  const openAddMenu = async (btn: HTMLButtonElement) => {
    const trackId = btn.dataset.addmenu!;
    const menu = btn.nextElementSibling as HTMLElement;
    const wasOpen = !menu.hidden;
    app.querySelectorAll<HTMLElement>(".addmenu").forEach((m) => (m.hidden = true));
    if (wasOpen) return;
    menu.hidden = false;
    menu.innerHTML = `<div class="dim" style="padding:8px 10px;">Loading\u2026</div>`;
    const pls = (await loadPlaylists()).filter((p) => p.id !== "liked");
    if (menu.hidden) return; // closed again while this was loading
    menu.innerHTML = `
      <button class="addmenuitem" data-addto="${esc(trackId)}:new">+ New playlist\u2026</button>
      ${pls.map((p) => `<button class="addmenuitem" data-addto="${esc(trackId)}:${esc(p.id)}">${esc(p.name)}</button>`).join("")}`;
  };
  // named (not an inline arrow passed straight to addEventListener) so cleanup below can remove exactly
  // this one - renderMusic() reruns from scratch on every visit to /music (route()'s cleanup?.() only
  // tears down the *previous* page's own listeners), so without this, revisiting /music would stack a
  // fresh duplicate listener on `app` (a single persistent, never-recreated element) every time,
  // eventually making every heart-click/add-to-playlist fire multiple times over.
  const musicDelegatedClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const heartBtn = target.closest<HTMLButtonElement>("[data-heart]");
    if (heartBtn) {
      e.preventDefault();
      e.stopPropagation();
      toggleHeart(heartBtn.dataset.heart!);
      return;
    }
    const addBtn = target.closest<HTMLButtonElement>("[data-addmenu]");
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      openAddMenu(addBtn).catch(() => {});
      return;
    }
    const addTo = target.closest<HTMLButtonElement>("[data-addto]");
    if (addTo) {
      e.preventDefault();
      e.stopPropagation();
      // track ids look like "tr:<hash>" (contain a colon themselves), playlist ids are plain hex / "new"
      const spec = addTo.dataset.addto!;
      const cut = spec.lastIndexOf(":");
      const trackId = spec.slice(0, cut);
      const plId = spec.slice(cut + 1);
      addTo.closest<HTMLElement>(".addmenu")!.hidden = true;
      (async () => {
        try {
          let targetId = plId;
          if (plId === "new") {
            const name = prompt("New playlist name");
            if (!name?.trim()) return;
            const p = await api<{ id: string }>("/api/music/playlists", { method: "POST", body: JSON.stringify({ name: name.trim() }) });
            playlistsCache = null;
            targetId = p.id;
          }
          await api(`/api/music/playlists/${encodeURIComponent(targetId!)}/tracks`, { method: "POST", body: JSON.stringify({ trackId }) });
          toast("Added to playlist");
          playlistsCache = null;
          renderSidebarLibrary();
          if (view.kind === "playlist" && view.id === targetId) drawPlaylistDetail(targetId!);
        } catch (e: any) {
          toast(e.message || "Couldn't add to playlist");
        }
      })();
      return;
    }
    const rm = target.closest<HTMLButtonElement>("[data-removefrom]");
    if (rm) {
      e.preventDefault();
      e.stopPropagation();
      const spec = rm.dataset.removefrom!; // "<playlistId>:<trackId>", and trackId itself is "tr:<hash>"
      const cut = spec.indexOf(":");
      const plId = spec.slice(0, cut);
      const trackId = spec.slice(cut + 1);
      rm.closest(".row")?.remove();
      api(`/api/music/playlists/${encodeURIComponent(plId!)}/tracks/${encodeURIComponent(trackId!)}`, { method: "DELETE" }).catch((e: any) =>
        toast(e.message || "Couldn't remove from playlist"),
      );
      return;
    }
    if (!target.closest(".addwrap")) app.querySelectorAll<HTMLElement>(".addmenu").forEach((m) => (m.hidden = true));
  };
  app.addEventListener("click", musicDelegatedClick);
  playHeartBtn.onclick = () => curTrackId && toggleHeart(curTrackId);
  const loadStarred = async () => {
    starredTracks = new Set(await api<string[]>("/api/music/starred-tracks").catch(() => []));
    if (curTrackId) setHeart(curTrackId, starredTracks.has(curTrackId)); // already playing by the time this resolved
  };

  /** "seamless" auto-download for a not-yet-owned album card (drawArtistDetail's "missing" list, from
   *  musicbrainz.ts's discography) - one click does what would otherwise be a manual "go to the tracker
   *  search box, search, pick a release, download, wait, come back and refresh" - mirrors bindShelf's
   *  quickplay cards (search+pick+start in one request, loading/error states on the card itself), just
   *  polling for "is it in the library yet" afterwards instead of getting something to play immediately
   *  (no piece-range streaming for music - see music/stream.ts's module doc comment). `artistId`: only
   *  refresh the page if the user is still looking at the same artist by the time this resolves. */
  const bindGetAlbum = (artistName: string, artistId: string) => {
    browseBox.querySelectorAll<HTMLElement>("[data-getalbum]").forEach((card) => {
      const albumName = card.dataset.getalbum!;
      const stateText = card.querySelector<HTMLElement>(".statetext")!;
      const stillHere = () => view.kind === "artist" && view.id === artistId;
      const refresh = () => stillHere() && drawArtistDetail(artistId);
      const poll = async (hash: string) => {
        if (!card.isConnected) return; // navigated away
        try {
          const s = await api<any>(
            `/api/music/getalbum/status?artist=${encodeURIComponent(artistName)}&album=${encodeURIComponent(albumName)}&hash=${encodeURIComponent(hash)}`,
          );
          if (s.status === "ready") {
            refresh();
            return;
          }
          stateText.textContent = s.progress ? `Downloading\u2026 ${Math.round(s.progress * 100)}%` : "Downloading\u2026";
        } catch {}
        setTimeout(() => poll(hash), 4000);
      };
      const go = async () => {
        if (card.classList.contains("loading")) return;
        card.classList.remove("error");
        card.classList.add("loading");
        stateText.textContent = "Searching trackers\u2026";
        try {
          const r = await api<any>("/api/music/getalbum", { method: "POST", body: JSON.stringify({ artist: artistName, album: albumName }) });
          if (r.status === "already-in-library") {
            refresh();
            return;
          }
          stateText.textContent = "Downloading\u2026";
          setTimeout(() => poll(r.hash ?? ""), 4000);
        } catch (e: any) {
          card.classList.remove("loading");
          card.classList.add("error");
          stateText.textContent = e.message || "Failed";
        }
      };
      card.onclick = go;
      card.onkeydown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          go();
        }
      };
    });
  };

  const drawArtists = async () => {
    browseBox.innerHTML = `<div class="center">Loading…</div>`;
    let artists: any[];
    try {
      artists = await api<any[]>("/api/music/artists");
    } catch (e: any) {
      if (alive()) browseBox.innerHTML = `<div class="center">${esc(e.message)}</div>`;
      return;
    }
    if (!alive() || view.kind !== "artists") return;
    if (!artists.length) {
      browseBox.innerHTML = `<div class="center">No music found yet.</div>`;
      return;
    }
    browseBox.innerHTML = `<div class="musiccards">${artists
      .map(
        (a) => `
      <div class="musiccard artist" data-artist="${esc(a.id)}" tabindex="0" role="button">
        <div class="poster"><img src="/media/music/${esc(a.id)}/artist-photo" alt="" loading="lazy" /><div class="noposter">${esc(a.name)}</div></div>
        <div class="ctitle">${esc(a.name)}</div>
        <div class="cmeta">${a.albumCount} album${a.albumCount === 1 ? "" : "s"}</div>
      </div>`,
      )
      .join("")}</div>`;
    bindArt();
    browseBox.querySelectorAll<HTMLElement>("[data-artist]").forEach((el) => {
      const go = () => openArtist(el.dataset.artist!);
      el.onclick = go;
      el.onkeydown = (e) => e.key === "Enter" && go();
    });
  };

  const drawArtistDetail = async (id: string) => {
    browseBox.innerHTML = `<div class="center">Loading…</div>`;
    let a: any;
    try {
      a = await api<any>(`/api/music/artist/${encodeURIComponent(id)}`);
    } catch (e: any) {
      if (alive()) browseBox.innerHTML = `<div class="center">${esc(e.message)}</div>`;
      return;
    }
    if (!alive() || view.kind !== "artist" || view.id !== id) return;
    const owned = (a.albums ?? [])
      .map(
        (al: any) => `
      <div class="musiccard" data-album="${esc(al.id)}" tabindex="0" role="button">
        <div class="poster"><img src="/media/music/${esc(al.id)}/cover" alt="" loading="lazy" />
          <button class="cardplay" data-playalbum="${esc(al.id)}" title="Play ${esc(al.name)}" aria-label="Play ${esc(al.name)}" tabindex="-1">▶</button>
        </div>
        <div class="ctitle">${esc(al.name)}</div>
        <div class="cmeta">${al.year ? `${al.year} · ` : ""}${al.trackCount} track${al.trackCount === 1 ? "" : "s"}</div>
      </div>`,
      )
      .join("");
    // "seamless", same spirit as the front page's TMDB-backed catalog rows: albums this artist has put
    // out that aren't downloaded yet (musicbrainz.ts's discography, merged server-side) show up right
    // alongside the ones already in the library - clicking one just works (searches trackers, picks a
    // release, downloads it - see bindGetAlbum below), no separate "go search trackers yourself" step.
    const missing = (a.missing ?? [])
      .map(
        (m: any) => `
      <div class="musiccard getalbum" data-getalbum="${esc(m.name)}" tabindex="0" role="button">
        <div class="poster">
          <div class="noposter getnote">${m.year ? esc(String(m.year)) : ""}<span class="geticon">⇩</span></div>
          <div class="state"><div class="spinner"></div><div class="statetext"></div></div>
        </div>
        <div class="ctitle">${esc(m.name)}</div>
        <div class="cmeta">${m.year ? `${m.year} · ` : ""}Get from trackers</div>
      </div>`,
      )
      .join("");
    // "Top songs": Last.fm's global play data (server.ts matches each one against this artist's own
    // library tracks already) - playable right away when it's something you have, shown as a plain
    // (not clickable) row with a "not in your library" note otherwise, same "never invents a fake
    // affordance" spirit as the missing-albums tiles only ever offering a real download, never a dead
    // link. Absent entirely (not even the heading) when Last.fm isn't configured for this user, or the
    // artist simply doesn't have any ranked tracks there - never a reason the rest of the page can't load.
    const topSongs: { title: string; trackId?: string }[] = a.topSongs ?? [];
    const topSongsHtml = topSongs.length
      ? `<h3>Top songs</h3>
         <div class="list">${topSongs
           .map(
             (t, i) => `
           <div class="row musictrack${t.trackId ? "" : " dim"}" data-topsong="${i}" tabindex="0" role="button">
             <div class="meta musictracknum"><span class="tracknum">${i + 1}</span>${t.trackId ? `<span class="trackplay">▶</span>` : ""}</div>
             <div class="name">${esc(t.title)}</div>
             ${t.trackId ? trackActionsHtml(t.trackId) : `<div class="meta hide-sm">not in your library</div>`}
           </div>`,
           )
           .join("")}</div>`
      : "";
    browseBox.innerHTML = `
      <div class="musicbreadcrumb"><a href="#" data-back>← Artists</a></div>
      <div class="musicartisthead">
        <div class="poster musicartistart"><img src="/media/music/${esc(a.id)}/artist-photo" alt="" loading="lazy" /><div class="noposter">${esc(a.name)}</div></div>
        <h3>${esc(a.name)}</h3>
      </div>
      ${topSongsHtml}
      <h3>Discography</h3>
      <div class="musiccards">${owned}${missing}</div>`;
    bindArt();
    bindCardPlay();
    bindGetAlbum(a.name, id);
    browseBox.querySelector("[data-back]")!.addEventListener("click", (e) => {
      e.preventDefault();
      backToArtists();
    });
    browseBox.querySelectorAll<HTMLElement>("[data-album]").forEach((el) => {
      const go = () => openAlbum(el.dataset.album!);
      el.onclick = go;
      el.onkeydown = (e) => e.key === "Enter" && go();
    });
    browseBox.querySelectorAll<HTMLElement>("[data-topsong]").forEach((el) => {
      const t = topSongs[Number(el.dataset.topsong)]!;
      if (!t.trackId) return; // nothing to play - leave it inert (no handler at all), not a dead click
      const go = (e: Event) => {
        if ((e.target as HTMLElement).closest("button")) return;
        playQueue([{ id: t.trackId!, title: t.title, artist: a.name }], 0);
      };
      el.onclick = go;
      el.onkeydown = (e) => e.key === "Enter" && go(e);
    });
  };

  const drawAlbumDetail = async (id: string) => {
    browseBox.innerHTML = `<div class="center">Loading…</div>`;
    let al: any;
    try {
      al = await api<any>(`/api/music/album/${encodeURIComponent(id)}`);
    } catch (e: any) {
      if (alive()) browseBox.innerHTML = `<div class="center">${esc(e.message)}</div>`;
      return;
    }
    if (!alive() || view.kind !== "album" || view.id !== id) return;
    const tracks: any[] = al.tracks ?? [];
    const toQueue = () => tracks.map((t) => ({ id: t.id, title: t.title, artist: al.artist, album: al.name, albumId: al.id, duration: t.duration }));
    browseBox.innerHTML = `
      <div class="musicbreadcrumb"><a href="#" data-backartist>← ${esc(al.artist)}</a></div>
      <div class="musicalbumhead">
        <div class="poster musicalbumart"><img src="/media/music/${esc(al.id)}/cover" alt="" loading="lazy" /></div>
        <div>
          <h3>${esc(al.name)}</h3>
          <div class="dim">${esc(al.artist)}${al.year ? ` · ${al.year}` : ""} · ${tracks.length} track${tracks.length === 1 ? "" : "s"}</div>
          <div class="musicactionbar">
            <button class="heroplay" id="musplayalbum" title="Play album" aria-label="Play album">▶</button>
          </div>
        </div>
      </div>
      <div class="list">${tracks
        .map(
          (t, i) => `
        <div class="row musictrack" data-i="${i}" data-trackid="${esc(t.id)}" tabindex="0" role="button">
          <div class="meta musictracknum"><span class="tracknum">${t.track ?? i + 1}</span><span class="trackplay">▶</span></div>
          <div class="name">${esc(t.title)}</div>
          <div class="meta">${fmtTime(t.duration)}</div>
          ${trackActionsHtml(t.id)}
        </div>`,
        )
        .join("")}</div>`;
    bindArt();
    browseBox.querySelector("[data-backartist]")!.addEventListener("click", (e) => {
      e.preventDefault();
      openArtist(al.artistId);
    });
    browseBox.querySelector("#musplayalbum")!.addEventListener("click", () => playQueue(toQueue(), 0));
    browseBox.querySelectorAll<HTMLElement>(".musictrack").forEach((rowEl) => {
      const go = (e: Event) => {
        if ((e.target as HTMLElement).closest("button")) return;
        playQueue(toQueue(), Number(rowEl.dataset.i));
      };
      rowEl.onclick = go;
      rowEl.onkeydown = (e) => e.key === "Enter" && go(e);
    });
  };

  // ---- sidebar library: "Your library" (Liked Songs pinned + every playlist) - unlike the old
  // separate "Playlists" page this replaced, it's always visible next to whatever's open in the main
  // pane, Spotify-style, so opening a playlist is one click from anywhere instead of a page away.
  const sideLib = $("mussidelib");
  const renderSidebarLibrary = async (force = false) => {
    let pls: PlaylistSummary[];
    try {
      pls = await loadPlaylists(force);
    } catch {
      return; // not fatal - the rest of the page still works, the sidebar list just stays stale/empty
    }
    if (!alive()) return;
    sideLib.innerHTML = pls
      .map(
        (p) => `
      <div class="mussideitem${p.special ? " liked" : ""}${view.kind === "playlist" && view.id === p.id ? " active" : ""}" data-playlist="${esc(p.id)}" tabindex="0" role="button">
        <span class="name">${p.special ? "\u2665 " : ""}${esc(p.name)}</span>
        ${p.special ? "" : `<button class="ghost round" data-delplaylist="${esc(p.id)}" title="Delete playlist" aria-label="Delete playlist">\u2715</button>`}
      </div>`,
      )
      .join("");
    sideLib.querySelectorAll<HTMLElement>("[data-playlist]").forEach((el) => {
      const go = (e: Event) => {
        if ((e.target as HTMLElement).closest("button")) return;
        openPlaylist(el.dataset.playlist!);
      };
      el.onclick = go;
      el.onkeydown = (e) => e.key === "Enter" && go(e);
    });
    sideLib.querySelectorAll<HTMLButtonElement>("[data-delplaylist]").forEach((b) => {
      b.onclick = async (e) => {
        e.stopPropagation();
        if (!confirm("Delete this playlist?")) return;
        try {
          await api(`/api/music/playlists/${encodeURIComponent(b.dataset.delplaylist!)}`, { method: "DELETE" });
          if (view.kind === "playlist" && view.id === b.dataset.delplaylist) backToArtists();
          renderSidebarLibrary(true);
        } catch (e: any) {
          toast(e.message || "Couldn't delete playlist");
        }
      };
    });
  };
  $<HTMLButtonElement>("musnewplaylist").onclick = async () => {
    const name = prompt("Playlist name");
    if (!name?.trim()) return;
    try {
      const p = await api<PlaylistSummary>("/api/music/playlists", { method: "POST", body: JSON.stringify({ name: name.trim() }) });
      playlistsCache = null;
      await renderSidebarLibrary(true);
      openPlaylist(p.id);
    } catch (e: any) {
      toast(e.message || "Couldn't create playlist");
    }
  };
  // Claude picks only from tracks actually in the library (server.ts's /api/music/ai-playlist, see
  // music/aiplaylist.ts) - never invents one - so this is always a real, playable playlist at the end.
  const aiPlaylistBtn = $<HTMLButtonElement>("musaiplaylist");
  aiPlaylistBtn.onclick = async () => {
    if (aiPlaylistBtn.disabled) return;
    const prompt_ = prompt("Describe a genre or vibe (e.g. \u201c90s grunge\u201d, \u201csomething for a rainy study session\u201d)");
    if (!prompt_?.trim()) return;
    aiPlaylistBtn.disabled = true;
    aiPlaylistBtn.textContent = "\u22ef";
    try {
      const p = await api<PlaylistSummary>("/api/music/ai-playlist", { method: "POST", body: JSON.stringify({ prompt: prompt_.trim() }) });
      playlistsCache = null;
      await renderSidebarLibrary(true);
      openPlaylist(p.id);
    } catch (e: any) {
      toast(e.message || "Couldn't generate a playlist");
    } finally {
      aiPlaylistBtn.disabled = false;
      aiPlaylistBtn.textContent = "\u2728";
    }
  };

  const drawPlaylistDetail = async (id: string) => {
    browseBox.innerHTML = `<div class="center">Loading\u2026</div>`;
    let pl: any;
    try {
      pl = await api<any>(`/api/music/playlists/${encodeURIComponent(id)}`);
    } catch (e: any) {
      if (alive()) browseBox.innerHTML = `<div class="center">${esc(e.message)}</div>`;
      return;
    }
    if (!alive() || view.kind !== "playlist" || view.id !== id) return;
    const tracks: MusicTrack[] = pl.tracks ?? [];
    browseBox.innerHTML = `
      <div class="musicalbumhead">
        <div class="playlistart${pl.special ? " liked" : ""}">${pl.special ? "\u2665" : "\u266a"}</div>
        <div>
          <h3>${esc(pl.name)}</h3>
          <div class="dim">${tracks.length} track${tracks.length === 1 ? "" : "s"}</div>
          <div class="musicactionbar">
            ${tracks.length ? `<button class="heroplay" id="musplayplaylist" title="Play" aria-label="Play">\u25b6</button>` : ""}
            ${pl.special ? "" : `<button class="ghost" id="musrenameplaylist">Rename</button>`}
          </div>
        </div>
      </div>
      ${
        tracks.length
          ? `<div class="list">${tracks
              .map(
                (t: any, i: number) => `
        <div class="row musictrack" data-i="${i}" data-trackid="${esc(t.id)}" tabindex="0" role="button">
          <div class="meta musictracknum"><span class="tracknum">${i + 1}</span><span class="trackplay">\u25b6</span></div>
          <div class="name">${esc(t.title)}<div class="meta">${esc(t.artist ?? "")}${t.album ? ` \u2014 ${esc(t.album)}` : ""}</div></div>
          <div class="meta hide-sm">${fmtTime(t.duration)}</div>
          ${trackActionsHtml(t.id)}
          ${pl.special ? "" : `<button class="ghost round rowaction" data-removefrom="${esc(id)}:${esc(t.id)}" title="Remove from playlist" aria-label="Remove from playlist">\u2715</button>`}
        </div>`,
              )
              .join("")}</div>`
          : `<div class="dim" style="padding:16px 0;">${pl.special ? "Songs you like will show up here \u2014 tap the heart on any track." : "No tracks yet \u2014 add some from an album or search."}</div>`
      }
      <div id="mussimilar"></div>`;
    // "You may also like": loads on its own (Claude, cached per playlist content - music/aiplaylist.ts's
    // recommendMore), same pattern as the watch page's movie/TV equivalent - never blocks the playlist
    // itself from showing, and simply doesn't appear at all if there's nothing to suggest.
    const simBox = browseBox.querySelector<HTMLElement>("#mussimilar")!;
    api<{ items: MusicTrack[] }>(`/api/music/playlists/${encodeURIComponent(id)}/similar`)
      .then((r) => {
        if (!alive() || view.kind !== "playlist" || view.id !== id || !r.items?.length) return;
        simBox.innerHTML = `
          <h3>You may also like</h3>
          <div class="list">${r.items
            .map(
              (t, i) => `
          <div class="row musiclink" data-simi="${i}" tabindex="0" role="button">
            <div class="name">${esc(t.title)}<div class="meta">${esc(t.artist ?? "")}${t.album ? ` \u2014 ${esc(t.album)}` : ""}</div></div>
            <div class="meta hide-sm">${t.duration != null ? fmtTime(t.duration) : ""}</div>
            ${trackActionsHtml(t.id)}
          </div>`,
            )
            .join("")}</div>`;
        simBox.querySelectorAll<HTMLElement>("[data-simi]").forEach((el) => {
          const go = (e: Event) => {
            if ((e.target as HTMLElement).closest("button")) return;
            playQueue(r.items, Number(el.dataset.simi));
          };
          el.onclick = go;
          el.onkeydown = (e) => e.key === "Enter" && go(e);
        });
      })
      .catch(() => {});
    browseBox.querySelector("#musplayplaylist")?.addEventListener("click", () => playQueue(tracks, 0));
    browseBox.querySelector("#musrenameplaylist")?.addEventListener("click", async () => {
      const name = prompt("Rename playlist", pl.name);
      if (!name?.trim() || name.trim() === pl.name) return;
      try {
        await api(`/api/music/playlists/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ name: name.trim() }) });
        playlistsCache = null;
        renderSidebarLibrary(true);
        drawPlaylistDetail(id);
      } catch (e: any) {
        toast(e.message || "Couldn't rename playlist");
      }
    });
    browseBox.querySelectorAll<HTMLElement>(".musictrack").forEach((rowEl) => {
      const go = (e: Event) => {
        if ((e.target as HTMLElement).closest("button")) return;
        playQueue(tracks, Number(rowEl.dataset.i));
      };
      rowEl.onclick = go;
      rowEl.onkeydown = (e) => e.key === "Enter" && go(e);
    });
  };

  const drawBrowse = () => {
    app.querySelectorAll<HTMLButtonElement>("[data-nav]").forEach((el) => {
      el.classList.toggle("active", view.kind === "artists" || view.kind === "artist" || view.kind === "album");
    });
    renderSidebarLibrary(); // cheap (cached unless a mutation just forced a refetch) - keeps the active-playlist highlight in sync on every navigation
    if (view.kind === "artists") return drawArtists();
    if (view.kind === "artist") return drawArtistDetail(view.id);
    if (view.kind === "album") return drawAlbumDetail(view.id);
    return drawPlaylistDetail(view.id);
  };
  function openArtist(id: string) {
    view = { kind: "artist", id };
    drawBrowse();
  }
  function openAlbum(id: string) {
    view = { kind: "album", id };
    drawBrowse();
  }
  function backToArtists() {
    view = { kind: "artists" };
    drawBrowse();
  }
  function openPlaylist(id: string) {
    view = { kind: "playlist", id };
    drawBrowse();
  }
  $<HTMLButtonElement>("musartistsnav").onclick = backToArtists;

  // ---- search: one input drives both your own library (fast, typo-tolerant - see searchdb.ts's
  // pg_trgm fuzzy search, served by /api/music/search) and, a little later (or on Enter),
  // "Ready to download" - the persistent download index the crawler keeps in Postgres (every listed
  // pack's name + crawled file lists, see packindex.ts/packdb.ts) plus Soulseek. The trackers
  // themselves are no longer queried per search. Reuses /api/search?cat=music +
  // /api/music/download and the generic file-peek helper (renderFileList) the front page's search
  // results use too - one merged results panel instead of two separate search boxes.
  const musq = $<HTMLInputElement>("musq");
  const resBox = $("musearch");
  const libBox = $("muslibresults");
  const trackerSection = $("mustrackersection");
  const trackerResults = $("mustrackerresults");
  const SOURCE_LABEL: Record<string, string> = { nb: "NorBits", ipt: "IPTorrents", td: "TorrentDay", slsk: "Soulseek" };
  const trackerFilesCache = new Map<number, { name: string; totalLength: number; files: { index: number; path: string; size: number }[] }>();
  let trackerHits: any[] = [];
  let trackerSearchSeq = 0;
  let libTimer: Timer | null = null;
  let trackerTimer: Timer | null = null;

  const doLibSearch = async () => {
    const q = musq.value.trim();
    if (!q) {
      libBox.innerHTML = "";
      return;
    }
    let r: any;
    try {
      r = await api<any>(`/api/music/search?q=${encodeURIComponent(q)}`);
    } catch {
      return;
    }
    if (!alive() || musq.value.trim() !== q) return;
    const artists = r.artists ?? [];
    const albums = r.albums ?? [];
    const tracks: MusicTrack[] = r.tracks ?? [];
    if (!artists.length && !albums.length && !tracks.length) {
      libBox.innerHTML = "";
      return;
    }
    libBox.innerHTML = `
      <h3>In your library</h3>
      ${
        artists.length
          ? `<div class="list">${artists
              .map((a: any) => `<div class="row musiclink" data-artist="${esc(a.id)}" tabindex="0" role="button"><div class="name">${esc(a.name)}<div class="meta">${a.albumCount} album${a.albumCount === 1 ? "" : "s"}</div></div></div>`)
              .join("")}</div>`
          : ""
      }
      ${
        albums.length
          ? `<div class="list">${albums
              .map((a: any) => `<div class="row musiclink" data-album="${esc(a.id)}" tabindex="0" role="button"><div class="name">${esc(a.name)}<div class="meta">${esc(a.artist)}${a.year ? ` · ${a.year}` : ""}</div></div></div>`)
              .join("")}</div>`
          : ""
      }
      ${
        tracks.length
          ? `<div class="list">${tracks
              .map((t, i) => `<div class="row musiclink" data-track="${i}" tabindex="0" role="button"><div class="name">${esc(t.title)}<div class="meta">${esc(t.artist ?? "")}${t.album ? ` — ${esc(t.album)}` : ""}${t.duration != null ? ` · ${fmtTime(t.duration)}` : ""}</div></div>${trackActionsHtml(t.id)}</div>`)
              .join("")}</div>`
          : ""
      }`;
    libBox.querySelectorAll<HTMLElement>("[data-artist]").forEach((el) => {
      const go = () => {
        musq.value = "";
        resBox.hidden = true;
        openArtist(el.dataset.artist!);
      };
      el.onclick = go;
      el.onkeydown = (e) => e.key === "Enter" && go();
    });
    libBox.querySelectorAll<HTMLElement>("[data-album]").forEach((el) => {
      const go = () => {
        musq.value = "";
        resBox.hidden = true;
        openAlbum(el.dataset.album!);
      };
      el.onclick = go;
      el.onkeydown = (e) => e.key === "Enter" && go();
    });
    libBox.querySelectorAll<HTMLElement>("[data-track]").forEach((el) => {
      const go = (e: Event) => {
        if ((e.target as HTMLElement).closest("button")) return;
        playQueue([tracks[Number(el.dataset.track)]!], 0);
      };
      el.onclick = go;
      el.onkeydown = (e) => e.key === "Enter" && go(e);
    });
  };

  // Soulseek downloads are a direct peer-to-peer transfer (src/slsk.ts) - unlike qBittorrent/torrents,
  // the server can't just "add it and forget", so the client polls a job id instead of getting a single
  // immediate answer. Resolves once the job reaches a final state (done or error).
  async function pollSlskJob(jobId: string, b: HTMLButtonElement) {
    for (;;) {
      await new Promise((r) => setTimeout(r, 1200));
      if (!alive()) return;
      let s: any;
      try {
        s = await api(`/api/music/soulseek/status?job=${encodeURIComponent(jobId)}`);
      } catch {
        continue; // transient - keep polling rather than giving up on one failed request
      }
      if (s.status === "done") {
        b.textContent = "Added \u2713";
        return;
      }
      if (s.status === "error") {
        b.disabled = false;
        b.textContent = "Download";
        alert(s.error || "Soulseek download failed");
        return;
      }
      if (s.status === "queued") b.textContent = s.queuePlace != null ? `Queued (#${s.queuePlace})\u2026` : "Queued\u2026";
      else if (s.status === "downloading") b.textContent = `Downloading ${Math.round((s.progress ?? 0) * 100)}%\u2026`;
      else b.textContent = "Connecting\u2026";
    }
  }

  const drawTrackerResults = () => {
    trackerSection.hidden = false;
    if (!trackerHits.length) {
      trackerResults.innerHTML = `<div class="dim">No results.</div>`;
      return;
    }
    trackerResults.innerHTML = trackerHits
      .map((r, i) => {
        const srcLabel = SOURCE_LABEL[r.source];
        const isSlsk = r.source === "slsk";
        // Soulseek has no seeders/leechers - a result is just one peer's copy of one file, with either a
        // free upload slot (download starts right away) or a queue behind their other uploads instead
        const availability = isSlsk
          ? `<span title="${r.slotsFree ? "peer has a free upload slot" : "peer is busy - download will queue"}">${r.slotsFree ? "● free slot" : "◐ queued"}</span>`
          : `<span title="availability">${r.seeders} ●</span>`;
        const extra = isSlsk && (r.bitrate || r.durationSec)
          ? `<div class="dim">${[r.bitrate ? `${r.bitrate}kbps` : "", r.durationSec ? fmtTime(r.durationSec) : ""].filter(Boolean).join(" · ")}</div>`
          : "";
        return `
      <div class="resultitem">
      <div class="row">
        <div class="name">${esc(r.name)}<span class="badge music" title="${srcLabel ? `from ${srcLabel}` : "TorrentLeech"}">🎵${srcLabel ? ` ${srcLabel}` : ""}</span>${r.freeleech ? `<span class="badge">free</span>` : ""}${
          r.matchedFile ? `<div class="dim">found inside: ${esc(r.matchedFile)}</div>` : extra
        }</div>
        <div class="meta hide-sm">${fmtSize(r.size)}</div>
        <div class="meta hide-sm">${availability}</div>
        ${isSlsk ? "" : `<button class="ghost" data-mfiles="${i}" title="look inside this torrent for a specific file">Files</button>`}
        <button data-mdl="${i}" ${!isSlsk && r.seeders === 0 ? "disabled" : ""}>Download</button>
      </div>
      <div class="filespanel" id="mfiles-${i}" hidden></div>
      </div>`;
      })
      .join("");
    trackerResults.querySelectorAll<HTMLButtonElement>("button[data-mdl]").forEach((b) => {
      b.onclick = async () => {
        const r = trackerHits[Number(b.dataset.mdl)];
        b.disabled = true;
        b.textContent = "Downloading…";
        try {
          const res: any = await api("/api/music/download", { method: "POST", body: JSON.stringify({ id: r.id, title: r.name }) });
          if (res.slskJob) await pollSlskJob(res.slskJob, b);
          else b.textContent = "Added ✓";
        } catch (e: any) {
          b.disabled = false;
          b.textContent = "Download";
          alert(e.message);
        }
      };
    });
    trackerResults.querySelectorAll<HTMLButtonElement>("button[data-mfiles]").forEach((b) => {
      b.onclick = async () => {
        const i = Number(b.dataset.mfiles);
        const r = trackerHits[i];
        const panel = document.getElementById(`mfiles-${i}`)!;
        if (!panel.hidden) {
          panel.hidden = true;
          return;
        }
        panel.hidden = false;
        if (panel.dataset.loaded) return;
        panel.innerHTML = `<div class="dim">Loading file list…</div>`;
        try {
          let data = trackerFilesCache.get(i);
          if (!data) {
            data = await api(`/api/search/files?id=${encodeURIComponent(r.id)}`);
            trackerFilesCache.set(i, data!);
          }
          panel.dataset.loaded = "1";
          renderFileList(panel, data!);
        } catch (e: any) {
          panel.innerHTML = `<div class="dim">Couldn't read the file list: ${esc(e.message)}</div>`;
        }
      };
    });
  };

  const doTrackerSearch = async () => {
    const q = musq.value.trim();
    if (!q) {
      trackerHits = [];
      trackerSection.hidden = true;
      trackerResults.innerHTML = "";
      return;
    }
    const seq = ++trackerSearchSeq;
    trackerSection.hidden = false;
    trackerResults.innerHTML = `<div class="center">Searching the index…</div>`;
    let r: any[];
    try {
      r = await api<any[]>(`/api/search?q=${encodeURIComponent(q)}&cat=music`);
    } catch (e: any) {
      if (alive() && seq === trackerSearchSeq) trackerResults.innerHTML = `<div class="center">${esc(e.message)}</div>`;
      return;
    }
    if (!alive() || seq !== trackerSearchSeq) return;
    trackerFilesCache.clear();
    trackerHits = r;
    drawTrackerResults();
  };
  musq.oninput = () => {
    resBox.hidden = !musq.value.trim();
    if (libTimer) clearTimeout(libTimer);
    libTimer = setTimeout(() => doLibSearch().catch(() => {}), 250);
    if (trackerTimer) clearTimeout(trackerTimer);
    if (!musq.value.trim()) {
      trackerHits = [];
      trackerSection.hidden = true;
      trackerResults.innerHTML = "";
      return;
    }
    // a little slower than the local library search (and skippable by just hitting Enter) - trackers
    // are slower, rate-limited third-party services, not worth hitting on every keystroke
    trackerTimer = setTimeout(() => doTrackerSearch().catch(() => {}), 700);
  };
  musq.onkeydown = (e) => {
    if (e.key !== "Enter" || !musq.value.trim()) return;
    e.preventDefault();
    if (trackerTimer) clearTimeout(trackerTimer);
    doTrackerSearch().catch(() => {});
  };

  cleanup = () => {
    if (scanPollTimer) clearInterval(scanPollTimer);
    if (libTimer) clearTimeout(libTimer);
    if (trackerTimer) clearTimeout(trackerTimer);
    app.removeEventListener("click", musicDelegatedClick);
    audioEl.pause();
    audioEl.removeAttribute("src");
    audioEl.load();
    document.body.classList.remove("hasplayer");
  };

  drawConnect();
  drawAdmin().catch(() => {});
  initKeys().catch(() => {});
  drawLastfm().catch(() => {});
  drawReco().catch(() => {});
  loadStarred().catch(() => {});
  renderSidebarLibrary().catch(() => {});
  await drawArtists();
}

// ---------------- Jellyfin apps: how to connect ----------------
async function renderApps() {
  const alive = pageGuard();
  const me = await fetch("/api/me").then((r) => r.json()).catch(() => ({}));
  if (!alive()) return;
  const server = location.origin;
  const user = me.user ?? "your name";
  const ok = `<span class="badge ok">works</span>`;
  const part = `<span class="badge part">partly</span>`;
  const untested = `<span class="badge">not tested yet</span>`;
  const no = `<span class="badge no">doesn't work</span>`;
  const row = (app: string, where: string, state: string, note: string) =>
    `<tr><td><b>${app}</b><div class="dim">${where}</div></td><td>${state}</td><td>${note}</td></tr>`;
  const table = (rows: string) => `<table class="apps"><tr><th>App</th><th>Status</th><th>Notes</th></tr>${rows}</table>`;
  app.innerHTML = `
    <div class="appsinfo">
      <h2>Jellyfin apps</h2>
      <p>This server also speaks the Jellyfin protocol, so you can watch with Jellyfin apps on phones, tablets and TVs:
      the same library, history and "continue watching" as here. Titles that aren't downloaded yet start downloading
      when you press play (the first start can take 10–30 s).</p>

      <div class="connect">
        <div><span class="dim">Server address</span><code>${esc(server)}</code></div>
        <div><span class="dim">User name</span><code>${esc(user)}</code></div>
        <div><span class="dim">Password</span><span>the one you use to sign in here</span></div>
      </div>
      <p class="dim">In the app choose "Add server" (or "Jellyfin"), enter the address, then the user name and password.
      If the app asks for a port, use 443 (https).</p>

      <h3>iPhone and iPad (iOS)</h3>
      ${table(
        row("Swiftfin", "App Store, free", ok, "Recommended. Browsing, episodes and playback work. Plays through VLC, so almost every file plays directly.") +
          row("Infuse", "App Store, free / Pro", part, "Signs in and browses fine, but playback currently hangs on a spinner. Being worked on.") +
          row("Streamyfin", "App Store, free", untested, "Native app; should work.") +
          row("Jellyfin Mobile", "App Store", no, "The official app only shows Jellyfin's own web interface, which this server doesn't have. Use Safari on this site instead."),
      )}

      <h3>Apple TV (tvOS)</h3>
      ${table(
        row("Swiftfin", "App Store, free", untested, "Same app as on iPhone; the best bet.") +
          row("Infuse", "App Store", part, "Same state as on iPhone (browses, playback hangs)."),
      )}

      <h3>Android phones and tablets</h3>
      ${table(
        row("Findroid", "Google Play / F-Droid, free", untested, "Native app; should work.") +
          row("Streamyfin", "Google Play, free", untested, "Native app; should work.") +
          row("Jellyfin (official)", "Google Play", no, "Shows Jellyfin's web interface, which this server doesn't have. Use Chrome on this site instead."),
      )}

      <h3>TVs</h3>
      ${table(
        row("Jellyfin for Android TV", "Android TV, Google TV, Fire TV", untested, "The official TV app is native; should work.") +
          row("This website", "Samsung (Tizen) TV browser", ok, "Open this site in the TV's browser - it's made to work there, including the remote.") +
          row("Jellyfin (official)", "Samsung Tizen, LG webOS", no, "These TV apps show Jellyfin's web interface, which this server doesn't have. Use the TV's browser instead."),
      )}

      <h3>Computers</h3>
      ${table(
        row("This website", "any browser", ok, "Nothing to install.") +
          row("jellytui", "terminal (Linux, macOS), with mpv", ok, "Tested: browsing, search and playback.") +
          row("Jellyfin Media Player", "Windows, macOS, Linux", no, "Shows Jellyfin's web interface, which this server doesn't have.") +
          row("Jellyfin for Kodi / JellyCon", "Kodi add-ons", untested, "Should work for browsing and direct play."),
      )}

      <h3>Good to know</h3>
      <ul>
        <li><b>Libraries:</b> Movies and Shows are what's downloaded here; <b>Discover</b> has popular titles you can play straight away.</li>
        <li><b>Mobile data:</b> 1080p needs about 5-8 Mbit/s. On a weak connection the video can stall - Wi-Fi is safer.</li>
        <li><b>Your own account:</b> the shared password signs you in as "Demo". For your own history, ask an admin for a user.</li>
        <li><b>Problems?</b> Tell Frank which app, which title, and roughly when - the server logs every app request.</li>
      </ul>
    </div>`;
}

async function renderStatus() {
  const alive = pageGuard();
  app.innerHTML = `
    <h2>Status</h2>
    <div class="statusgrid" id="summary"><div class="dim">Loading…</div></div>
    <div style="margin-top:10px"><a href="/status/grafana/d/torrstream?orgId=1&refresh=5s&theme=dark" target="_blank" rel="noopener">Open the dashboard in a new tab ↗</a></div>
    ${
      modernBrowser
        ? `<iframe class="grafana" src="/status/grafana/d/torrstream?orgId=1&kiosk&refresh=5s&theme=dark" title="Status dashboard" loading="lazy"></iframe>`
        : `<div class="dim" style="margin-top:14px">The detailed dashboard needs a newer browser (PC, phone, Apple TV) - the summary above updates by itself.</div>`
    }`;
  const kinds: Record<string, string> = { direct: "direct", remux: "HD", transcode: "converted", file: "AirPlay" };
  const draw = async () => {
    const s = await api<any>("/api/status").catch(() => null);
    if (!alive()) return;
    const box = app.querySelector("#summary");
    if (!s || !box) return;
    box.innerHTML = `
      <div class="scard"><div class="snum">${s.streams.length}</div><div class="dim">watching now</div></div>
      <div class="scard"><div class="snum">${fmtSpeed(s.download)}</div><div class="dim">download</div></div>
      <div class="scard"><div class="snum">${fmtSpeed(s.upload)}</div><div class="dim">upload (seeding)</div></div>
      <div class="scard"><div class="snum">${s.downloading}</div><div class="dim">downloading · ${s.library} in library</div></div>
      <div class="scard wide">
        <div class="dim">Now playing</div>
        ${
          s.streams.length
            ? s.streams.map((x: any) => `<div><b>${esc(x.user)}</b> · ${esc(x.title)} <span class="badge">${esc(kinds[x.kind] ?? x.kind.replace(/^hls-/, "Apple "))}</span></div>`).join("")
            : `<div class="dim">nobody right now</div>`
        }
      </div>
      <div class="scard wide">
        <div class="dim">Trackers</div>
        ${s.trackers.map((t: any) => `<span class="tstat"><span class="dot ${t.ok === null ? "unknown" : t.ok ? "ok" : "bad"}"></span>${esc(t.name)}</span>`).join("")}
        <div class="dim" style="margin-top:6px">cross-seeding: ${Object.entries(s.crossSeeding).map(([k, v]) => `${k === "td" ? "TorrentDay" : k === "nb" ? "NorBits" : k === "ipt" ? "IPTorrents" : k} ${v}`).join(" · ") || "-"}</div>
        ${
          s.packIndex?.max > 0
            ? `<div class="dim" style="margin-top:4px">music pack index (find a song inside an album): ${s.packIndex.indexed.toLocaleString()} / ${s.packIndex.max.toLocaleString()} packs</div>`
            : ""
        }
      </div>`;
  };
  const iv = setInterval(() => draw().catch(() => {}), 5000);
  cleanup = () => clearInterval(iv);
  await draw();
}

// ---------------- users (admin) ----------------
function agoMs(t?: number) {
  if (!t) return "never";
  const d = (Date.now() - t) / 1000;
  if (d < 120) return "just now";
  if (d < 3600) return `${Math.round(d / 60)} min ago`;
  if (d < 86400) return `${Math.round(d / 3600)} h ago`;
  if (d < 30 * 86400) return `${Math.round(d / 86400)} d ago`;
  return new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
async function renderUsers() {
  const alive = pageGuard();
  app.innerHTML = `<div class="center">Loading…</div>`;
  const day = (t?: number) => (t ? new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "never");
  const draw = async (msg = "") => {
    let list: any[];
    try {
      list = await api<any[]>("/api/users");
    } catch (e: any) {
      if (alive()) app.innerHTML = `<div class="center">${esc(e.message)}</div>`;
      return;
    }
    if (!alive()) return;
    const me = document.getElementById("whoami")!.textContent;
    app.innerHTML = `
      <h2>Users</h2>
      <div class="dim" style="margin-bottom:12px">Everyone signs in with their own password only. The shared password signs in as the shared "Demo" user.</div>
      <div class="list">${list
        .map(
          (u) => `
        <div class="row user" data-key="${esc(u.key)}">
          <div class="name">
            <b>${esc(u.name)}</b>${u.admin ? ` <span class="badge">admin</span>` : ""}${u.name === me ? ` <span class="badge">you</span>` : ""}
            <div class="meta" title="last sign-in: ${day(u.lastLoginAt)}">last active <b>${agoMs(u.lastActiveAt)}</b> · created ${day(u.createdAt)}</div>
          </div>
          <button class="ghost" data-act="rename">Rename</button>
          <button class="ghost" data-act="password">New password</button>
          <button class="ghost" data-act="admin">${u.admin ? "Remove admin" : "Make admin"}</button>
          <button class="ghost" data-act="delete" title="Delete user">✕</button>
        </div>`,
        )
        .join("")}</div>
      <h3 style="margin-top:22px">Tracker sessions</h3>
      <div class="dim" style="margin-bottom:8px">Checked every 30 minutes. TorrentDay and TC-Boxing sign in with a browser cookie (their login has a captcha) - when it expires, paste a new one here.</div>
      <div class="list" id="trackers"><div class="dim">Loading…</div></div>
      <h3 style="margin-top:22px">Add user</h3>
      <form class="searchbar" id="adduser">
        <input name="name" placeholder="Name" autocomplete="off" />
        <input name="password" type="password" placeholder="Their password" autocomplete="new-password" />
        <button>Add</button>
      </form>
      <div class="dim" id="umsg">${esc(msg)}</div>`;
    const umsg = app.querySelector("#umsg")!;
    const call = async (method: string, key: string, body?: object) => {
      try {
        await api(`/api/users/${encodeURIComponent(key)}`, { method, body: body ? JSON.stringify(body) : undefined });
        return true;
      } catch (e: any) {
        umsg.textContent = e.message;
        return false;
      }
    };
    app.querySelectorAll<HTMLElement>(".row.user").forEach((row) => {
      const key = row.dataset.key!;
      const u = list.find((x) => x.key === key)!;
      row.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((b) => {
        b.onclick = async () => {
          const act = b.dataset.act;
          if (act === "rename") {
            const name = prompt("New name", u.name);
            if (name && name !== u.name && (await call("PATCH", key, { name }))) draw(`Renamed to ${name}`);
          } else if (act === "password") {
            const pw = prompt(`New password for ${u.name} (min 6 characters)`);
            if (pw && (await call("PATCH", key, { password: pw }))) draw(`Password changed for ${u.name}`);
          } else if (act === "admin") {
            if (await call("PATCH", key, { admin: !u.admin })) draw();
          } else if (act === "delete") {
            if (confirm(`Delete ${u.name}? (their watch history stays in the library data)`) && (await call("DELETE", key))) draw(`Deleted ${u.name}`);
          }
        };
      });
    });
    // ---- tracker sessions
    const ago = (t: number) => {
      if (!t) return "never";
      const m = Math.round((Date.now() - t) / 60000);
      return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 48 * 60 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
    };
    const drawTrackers = async () => {
      const box = app.querySelector<HTMLElement>("#trackers");
      if (!box) return;
      let ts: any[];
      try {
        ts = await api<any[]>("/api/trackers");
      } catch (e: any) {
        box.innerHTML = `<div class="dim">${esc(e.message)}</div>`;
        return;
      }
      box.innerHTML = ts
        .map(
          (t) => `
        <div class="row tracker" data-id="${t.id}">
          <div class="name">
            <span class="dot ${!t.enabled ? "off" : t.ok === null ? "unknown" : t.ok ? "ok" : "bad"}"></span>
            <b>${esc(t.label)}</b> <span class="dim">(${t.auth === "cookie" ? `cookie${t.cookieSource === "admin" ? ", set here" : ", from config"}` : "username/password"})</span>
            <div class="meta">${
              !t.enabled ? "not configured" : t.ok === null ? "not checked yet" : t.ok ? `signed in · checked ${ago(t.checkedAt)}` : `<span class="warn">${esc(t.error || "failed")}</span> · last OK ${ago(t.lastOkAt)}`
            }</div>
            ${t.auth === "cookie" ? `<div class="cookieform" hidden><input name="cookie" placeholder="uid=12345; pass=abcdef…" autocomplete="off" /> <button data-save="${t.id}">Save</button></div>` : ""}
          </div>
          ${t.auth === "cookie" ? `<button class="ghost" data-newcookie="${t.id}">New cookie</button>` : ""}
          <button class="ghost" data-check="${t.id}">Check now</button>
        </div>`,
        )
        .join("");
      box.querySelectorAll<HTMLButtonElement>("button[data-check]").forEach((b) => {
        b.onclick = async () => {
          b.disabled = true;
          b.textContent = "Checking…";
          await api(`/api/trackers/${b.dataset.check}/check`, { method: "POST" }).catch(() => {});
          drawTrackers();
        };
      });
      box.querySelectorAll<HTMLButtonElement>("button[data-newcookie]").forEach((b) => {
        b.onclick = () => {
          const f = b.closest(".row")!.querySelector<HTMLElement>(".cookieform")!;
          f.hidden = !f.hidden;
          f.querySelector("input")?.focus();
        };
      });
      box.querySelectorAll<HTMLButtonElement>("button[data-save]").forEach((b) => {
        b.onclick = async () => {
          const input = b.parentElement!.querySelector("input")!;
          b.disabled = true;
          try {
            await api(`/api/trackers/${b.dataset.save}/cookie`, { method: "POST", body: JSON.stringify({ cookie: input.value }) });
            umsg.textContent = "Cookie saved and checked";
          } catch (e: any) {
            umsg.textContent = e.message;
          }
          drawTrackers();
        };
      });
    };
    drawTrackers();

    const form = app.querySelector<HTMLFormElement>("#adduser")!;
    form.onsubmit = async (e) => {
      e.preventDefault();
      const name = (form.elements.namedItem("name") as HTMLInputElement).value;
      const password = (form.elements.namedItem("password") as HTMLInputElement).value;
      try {
        await api("/api/users", { method: "POST", body: JSON.stringify({ name, password }) });
        draw(`Added ${name}`);
      } catch (err: any) {
        umsg.textContent = err.message;
      }
    };
  };
  await draw();
}

// ---------------- quality ----------------
const QKEY = "ts.quality"; // "auto" | "orig" | "<height>"
// audio language chosen per title ("tv:37854" -> "en"): the next episode plays the same dub / original
const ALANG_KEY = "ts.alang";
const audioPrefs = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem(ALANG_KEY) || "{}") ?? {};
  } catch {
    return {};
  }
};
const setAudioPref = (key: string, lang: string) => {
  const p = audioPrefs();
  p[key] = lang;
  localStorage.setItem(ALANG_KEY, JSON.stringify(p));
};
let resumeOverride: { hash: string; file: number; t: number } | null = null;

/** Client download speed in bits/s: time a short download (max ~3 s), cached 10 min. */
async function clientSpeed(): Promise<number> {
  try {
    const c = JSON.parse(sessionStorage.getItem("ts.speed") || "null");
    if (c && Date.now() - c.at < 600_000) return c.bps;
  } catch {}
  const t0 = performance.now();
  let bytes = 0;
  try {
    const res = await fetch("/api/speedtest", { cache: "no-store" });
    const reader = res.body!.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      bytes += value.byteLength;
      if (performance.now() - t0 > 3000) {
        reader.cancel().catch(() => {});
        break;
      }
    }
  } catch {}
  const secs = Math.max(0.05, (performance.now() - t0) / 1000);
  const bps = (bytes * 8) / secs;
  try {
    sessionStorage.setItem("ts.speed", JSON.stringify({ at: Date.now(), bps }));
  } catch {}
  return bps;
}

/** Auto: highest level that fits both the screen and the connection. 0 = original. */
async function autoLevel(probe: any): Promise<number> {
  const ladder: { h: number; kbps: number }[] = probe.ladder ?? [];
  if (!ladder.length) return 0;
  const conn = (navigator as any).connection;
  if (conn?.saveData) return ladder[ladder.length - 1]!.h;
  const srcH = probe.video?.height ?? 1080;
  // the short side of the screen is what a fullscreen landscape video gets
  const screenH = Math.round(Math.min(screen.width || 1920, screen.height || 1080) * (window.devicePixelRatio || 1));
  const bps = await clientSpeed();
  const levels = [{ h: srcH, bps: probe.bitrate || 8e6, q: 0 }, ...ladder.map((l) => ({ h: l.h, bps: (l.kbps + 200) * 1000, q: l.h }))];
  // smallest level still at least as sharp as the screen (never upscale beyond what the screen shows)
  const sharpEnough = levels.filter((l) => l.h >= screenH * 0.9);
  const maxUseful = sharpEnough.length ? sharpEnough[sharpEnough.length - 1]!.h : srcH;
  for (const l of levels) {
    if (l.h > maxUseful) continue;
    if (l.bps * 1.3 <= bps) return l.q;
  }
  return levels[levels.length - 1]!.q;
}

/** a season pack is named after the episode that first fetched it ("Lioness S01E03"): show the file's episode */
function playingTitle(title: string, path?: string): string {
  const m = path ? /S(\d{1,2})[ ._-]?E(\d{1,3})/i.exec(path.split("/").pop()!) : null;
  if (!m) return title;
  const tag = `S${m[1]!.padStart(2, "0")}E${m[2]!.padStart(2, "0")}`;
  if (/S\d{1,2}[ ._-]?E\d{1,3}/i.test(title)) return title.replace(/S\d{1,2}[ ._-]?E\d{1,3}/i, tag);
  return `${title.replace(/\s+S\d{1,2}$/i, "")} ${tag}`;
}

// ---- info panel on the player page
const personLink = (c: { id?: number; name: string }) =>
  c.id ? `<a href="/person/${c.id}" data-link class="plink">${esc(c.name)}</a>` : esc(c.name);
const fmtDate = (d?: string) =>
  d ? new Date(d + "T12:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";
const fmtRuntime = (min?: number) => (!min ? "" : min >= 60 ? `${Math.floor(min / 60)} h ${min % 60 ? `${min % 60} min` : ""}`.trim() : `${min} min`);
const fmtCount = (n?: number) => (!n ? "" : n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));
function infoHtml(i: any): string {
  const dot = (xs: string[]) => xs.filter(Boolean).join(" · ");
  const meta = dot([
    i.vote ? `★ ${Number(i.vote).toFixed(1)}${i.voteCount ? ` <span class="dim">(${fmtCount(i.voteCount)})</span>` : ""}` : "",
    i.type === "movie" ? fmtRuntime(i.runtime) : i.seasons ? `${i.seasons} season${i.seasons === 1 ? "" : "s"}` : "",
    esc((i.genres ?? []).slice(0, 3).join(", ")),
    i.type === "movie" ? (i.date ? `released ${fmtDate(i.date)}` : "") : esc(i.status ?? ""),
    esc((i.networks ?? []).slice(0, 2).join(", ")),
  ]);
  const ep = i.episode;
  const epHtml = ep
    ? `<div class="infoep">
        <div><b>S${String(ep.season).padStart(2, "0")}E${String(ep.episode).padStart(2, "0")}${ep.name ? ` · ${esc(ep.name)}` : ""}</b>
          <span class="dim">${dot([ep.airDate ? `aired ${fmtDate(ep.airDate)}` : "", fmtRuntime(ep.runtime)])}</span></div>
        ${ep.overview ? `<p>${esc(ep.overview)}</p>` : ""}
      </div>`
    : "";
  const makers = (i.makers ?? []).slice(0, 3);
  const cast = (i.cast ?? []).slice(0, 6);
  const links = [
    i.imdb ? `<a href="https://www.imdb.com/title/${esc(i.imdb)}/" target="_blank" rel="noopener">IMDb</a>` : "",
    `<a href="https://www.themoviedb.org/${i.type}/${i.tmdbId}" target="_blank" rel="noopener">TMDB</a>`,
  ];
  return `
    <div class="infobox">
      ${i.poster ? `<img class="infoposter" src="${esc(String(i.poster).replace("/w342/", "/w185/"))}" alt="">` : ""}
      <div class="infotext">
        <div class="infotitle">${esc(i.title)}${i.year ? ` <span class="dim">(${i.year})</span>` : ""}</div>
        ${meta ? `<div class="infometa">${meta}</div>` : ""}
        ${epHtml}
        ${i.tagline ? `<div class="infotag">${esc(i.tagline)}</div>` : ""}
        ${i.overview ? `<p>${ep ? `<span class="dim">About the show:</span> ` : ""}${esc(i.overview)}</p>` : ""}
        ${makers.length ? `<div><span class="dim">${i.type === "tv" ? "Created by" : "Directed by"}</span> ${makers.map(personLink).join(", ")}</div>` : ""}
        ${cast.length ? `<div><span class="dim">Starring</span> ${cast.map((c: any) => `${personLink(c)}${c.character ? ` <span class="dim">(${esc(c.character)})</span>` : ""}`).join(", ")}</div>` : ""}
        <div class="infolinks">${links.filter(Boolean).join(" · ")}</div>
      </div>
    </div>`;
}

// ---- My favs: hearted titles (shows: all seasons are auto-downloaded)
async function renderFavs() {
  const alive = pageGuard();
  app.innerHTML = `<div class="center">Loading…</div>`;
  const draw = async () => {
    const items = await api<any[]>("/api/favs").catch(() => null);
    if (!alive()) return;
    if (!items) return void (app.innerHTML = `<div class="center">Couldn't load your favs.</div>`);
    if (!items.length) {
      app.innerHTML = `<h2>My favs</h2><div class="dim">Nothing yet. Tap ♡ on the player page (or in History) to add a movie or show. Hearted shows get all their seasons downloaded automatically.</div>`;
      return;
    }
    const rows = [
      { title: `Shows · ${items.filter((i) => i.type === "tv").length}`, items: items.filter((i) => i.type === "tv") },
      { title: `Movies · ${items.filter((i) => i.type === "movie").length}`, items: items.filter((i) => i.type === "movie") },
    ].filter((r) => r.items.length);
    app.innerHTML = `
      <h2>My favs</h2>
      <div class="dim">Shows you heart are downloaded automatically: finished seasons as season packs, new episodes as they air. They keep seeding.</div>
      <div id="favrows"></div>
      <h3 class="alltitle">Manage</h3>
      <div class="list">${items
        .map(
          (i) => `<div class="row"><div class="name">${esc(i.title)}${i.year ? ` <span class="dim">(${i.year})</span>` : ""}<div class="meta">${i.type === "tv" ? esc(i.reason ?? "") : "movie"}</div></div>
            <button class="ghost heart on" data-unheart="${i.type}:${i.tmdbId}" title="Remove from My favs">♥</button></div>`,
        )
        .join("")}</div>`;
    const box = app.querySelector("#favrows")!;
    box.innerHTML = rows.map(tmdbShelf).join("");
    Array.prototype.forEach.call(box.children, (sec: Element, i: number) => bindShelf(sec, rows[i]));
    app.querySelectorAll<HTMLButtonElement>("button[data-unheart]").forEach((b) => {
      b.onclick = async () => {
        const [type, id] = b.dataset.unheart!.split(":");
        const it = items.find((x) => x.type === type && String(x.tmdbId) === id);
        await api("/api/rating", { method: "POST", body: JSON.stringify({ type, tmdbId: Number(id), title: it?.title, year: it?.year, rating: 0 }) }).catch(() => {});
        sugCache = null;
        draw();
      };
    });
  };
  await draw();
}

// ---- TV: IPTorrents IPTV (admins only)
const TV_FIXED = `<option value="trending">Trending now</option><option value="guide">TV guide</option><option value="mine">My IPTorrents list</option>`;
const hhmm = (t: number) => new Date(t).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
async function renderTV() {
  const alive = pageGuard();
  app.innerHTML = `
    <h2>TV</h2>
    <div class="tvplayer" id="tvplayer" hidden>
      <video id="tvv" controls playsinline autoplay x-webkit-airplay="allow"></video>
      <div class="tvnow"><b id="tvname"></b> <span class="dim" id="tvmsg"></span>
        <button class="ghost" id="tvair" hidden>AirPlay</button> <button class="ghost" id="tvstop">Stop</button></div>
      <div class="tvprog" id="tvprog"></div>
    </div>
    <div class="dim" id="tvslots"></div>
    <div class="tvbar">
      <select id="tvfolder">${TV_FIXED}</select>
      <input id="tvq" type="search" placeholder="Search all channels" autocomplete="off">
    </div>
    <div class="tvlist" id="tvlist"><div class="dim">Loading…</div></div>`;
  const $ = <T extends HTMLElement>(id: string) => app.querySelector<T>("#" + id)!;
  const v = $<HTMLVideoElement>("tvv");
  const list = $("tvlist");
  const folderSel = $<HTMLSelectElement>("tvfolder");
  const q = $<HTMLInputElement>("tvq");
  let playing: number | null = null;
  let info: any = null;

  const drawSlots = () => {
    if (!info) return;
    const used = info.sessions.map((s: any) => `${esc(s.name)} <span class="dim">(${esc(s.users.join(", "))})</span>`);
    $("tvslots").innerHTML =
      `IPTV connections: <b>${info.sessions.length} of ${info.slots}</b> in use${used.length ? ": " + used.join(" · ") : ""}` +
      (info.refreshing ? ` · <span class="dim">reading the channel list from IPTorrents…</span>` : "") +
      (info.error ? ` · <span class="warn">${esc(info.error)}</span>` : "");
  };
  const loadInfo = async () => {
    info = await api<any>("/api/iptv").catch(() => null);
    if (!alive() || !info) return;
    drawSlots();
    if (folderSel.options.length < 2 && info.folders?.length) {
      folderSel.innerHTML =
        TV_FIXED +
        info.folders.map((f: any) => `<option value="${f.id}">${esc(f.name)} (${f.count})</option>`).join("");
    }
    q.placeholder = info.channels ? `Search ${info.channels.toLocaleString()} channels` : "Search all channels";
  };

  const show = (chs: any[], empty: string, asGuide = false) => {
    if (!alive()) return;
    const progLine = (c: any) =>
      c.now || c.next
        ? `<div class="tvon">${c.now ? `<b>${esc(c.now.title)}</b> <span class="dim">until ${hhmm(c.now.stop)}</span>` : ""}${
            c.next ? `${c.now ? " · " : ""}<span class="dim">${hhmm(c.next.start)}</span> ${esc(c.next.title)}` : ""
          }</div>`
        : "";
    list.classList.toggle("asguide", asGuide);
    list.innerHTML = chs.length
      ? chs
          .map((c) =>
            asGuide
              ? `<div class="tvrow${c.id === playing ? " on" : ""}" data-id="${c.id}" tabindex="0" role="button"><div class="tvrowname">${c.logo ? `<img src="${esc(c.logo)}" alt="" loading="lazy">` : ""}<span>${esc(c.name)}</span></div><div class="tvrowprogs">${(c.upcoming ?? [])
                  .slice(0, 8)
                  .map((p: any) => `<span class="tvp${p.start <= Date.now() ? " now" : ""}" title="${esc(p.desc ?? "")}"><span class="dim">${hhmm(p.start)}</span> ${esc(p.title)}</span>`)
                  .join("")}</div></div>`
              : `<button class="tvch${c.id === playing ? " on" : ""}" data-id="${c.id}" title="${esc(c.now?.desc ?? "")}">${c.logo ? `<img src="${esc(c.logo)}" alt="" loading="lazy">` : ""}<span>${esc(c.name)}</span>${c.hot ? ` <span class="badge warn">hot</span>` : ""}${progLine(c)}</button>`,
          )
          .join("")
      : `<div class="dim">${empty}</div>`;
    list.querySelectorAll<HTMLElement>(".tvch,.tvrow").forEach((b) => {
      const go = () => start(Number(b.dataset.id), b.querySelector("span")!.textContent!);
      b.onclick = go;
      b.onkeydown = (e) => e.key === "Enter" && go();
    });
  };
  const loadList = async () => {
    const term = q.value.trim();
    const f = folderSel.value;
    const url = term
      ? `/api/iptv/channels?q=${encodeURIComponent(term)}`
      : f === "trending" || f === "mine" || f === "guide"
        ? `/api/iptv/channels?${f}=1`
        : `/api/iptv/channels?folder=${f}`;
    list.innerHTML = `<div class="dim">Loading…</div>`;
    const chs = await api<any[]>(url).catch(() => []);
    if (!term && f === "guide") return show(chs, "No guide data yet - it's fetched from IPTorrents every 2 hours.", true);
    show(
      chs,
      term ? "No channels match." : f === "mine" ? "Your IPTorrents channel list is empty - add channels on IPTorrents' IPTV page." : info?.channels ? "Nothing here." : "The channel list isn't loaded yet - it's read from IPTorrents in the background (a few minutes).",
    );
  };

  const drawProg = async () => {
    if (playing === null) return;
    const id = playing;
    const progs = await api<any[]>(`/api/iptv/guide/${id}`).catch(() => []);
    if (!alive() || playing !== id) return;
    const now = progs.find((p) => p.start <= Date.now());
    const next = progs.find((p) => p.start > Date.now());
    $("tvprog").innerHTML = [
      now ? `<div><b>Now:</b> ${esc(now.title)} <span class="dim">${hhmm(now.start)}–${hhmm(now.stop)}</span>${now.desc ? `<div class="dim">${esc(now.desc)}</div>` : ""}</div>` : "",
      next ? `<div><b>Next:</b> ${esc(next.title)} <span class="dim">${hhmm(next.start)}</span></div>` : "",
    ].join("");
  };
  const start = async (id: number, name: string) => {
    $("tvplayer").hidden = false;
    $("tvname").textContent = name;
    $("tvmsg").textContent = "starting…";
    $("tvprog").innerHTML = "";
    try {
      const r = await api<any>(`/api/iptv/play/${id}?caps=${encodeURIComponent(CAPS)}`, { method: "POST" });
      if (!alive()) return;
      playing = id;
      v.src = NATIVE_HLS ? r.hls : r.mp4;
      v.play().catch(() => {});
      list.querySelectorAll(".tvch,.tvrow").forEach((b) => b.classList.toggle("on", (b as HTMLElement).dataset.id === String(id)));
      drawProg();
    } catch (e: any) {
      $("tvmsg").textContent = e.message;
    }
    loadInfo();
  };
  const stop = () => {
    if (playing !== null) api(`/api/iptv/leave/${playing}`, { method: "POST" }).catch(() => {});
    playing = null;
    v.pause();
    v.removeAttribute("src");
    v.load();
    $("tvplayer").hidden = true;
    list.querySelectorAll(".tvch.on,.tvrow.on").forEach((b) => b.classList.remove("on"));
    setTimeout(loadInfo, 500);
  };
  $("tvstop").onclick = stop;
  v.addEventListener("playing", () => ($("tvmsg").textContent = ""));
  v.addEventListener("waiting", () => ($("tvmsg").textContent = "buffering…"));
  v.addEventListener("error", () => {
    if (playing !== null) $("tvmsg").textContent = "the stream stopped - pick the channel again";
  });
  if ((window as any).WebKitPlaybackTargetAvailabilityEvent) {
    const air = $<HTMLButtonElement>("tvair");
    air.hidden = false;
    air.onclick = () => (v as any).webkitShowPlaybackTargetPicker?.();
  }

  let deb: any = 0;
  q.addEventListener("input", () => {
    clearTimeout(deb);
    deb = setTimeout(loadList, 300);
  });
  folderSel.onchange = () => {
    q.value = "";
    loadList();
  };
  const iv = setInterval(() => loadInfo().catch(() => {}), 10_000);
  const ivProg = setInterval(() => drawProg().catch(() => {}), 60_000);
  cleanup = () => {
    clearInterval(iv);
    clearInterval(ivProg);
    clearTimeout(deb);
    if (playing !== null) api(`/api/iptv/leave/${playing}`, { method: "POST" }).catch(() => {});
    v.pause();
  };
  await loadInfo();
  await loadList();
}

// ---- a cast / crew member: who they are + rows of what they're in (cards play like on the front page)
async function renderPerson(id: number) {
  const alive = pageGuard();
  app.innerHTML = `<div class="center">Loading…</div>`;
  let p: any;
  try {
    p = await api<any>(`/api/person/${id}`);
  } catch (e: any) {
    if (alive()) app.innerHTML = `<div class="center">${esc(e.message)}</div>`;
    return;
  }
  if (!alive()) return;
  const age = (from: string, to?: string) => {
    const a = new Date(from), b = to ? new Date(to) : new Date();
    let y = b.getFullYear() - a.getFullYear();
    if (b.getMonth() < a.getMonth() || (b.getMonth() === a.getMonth() && b.getDate() < a.getDate())) y--;
    return y;
  };
  const life = [
    p.birthday ? `born ${fmtDate(p.birthday)}${p.deathday ? "" : ` (${age(p.birthday)})`}` : "",
    p.deathday ? `died ${fmtDate(p.deathday)}${p.birthday ? ` (${age(p.birthday, p.deathday)})` : ""}` : "",
    p.place ? esc(p.place) : "",
  ].filter(Boolean).join(" · ");
  const bio: string = p.bio ?? "";
  const short = bio.length > 600 ? bio.slice(0, bio.lastIndexOf(" ", 600)) + "…" : bio;
  const rows = [
    { title: "Known for", items: p.knownFor ?? [] },
    { title: `Movies · ${p.movies?.length ?? 0}`, items: p.movies ?? [] },
    { title: `TV · ${p.tv?.length ?? 0}`, items: p.tv ?? [] },
  ].filter((r) => r.items.length);
  app.innerHTML = `
    <div class="infobox person">
      ${p.profile ? `<img class="infoposter" src="${esc(p.profile)}" alt="">` : ""}
      <div class="infotext">
        <div class="infotitle">${esc(p.name)}</div>
        <div class="infometa">${[p.department ? esc(p.department) : "", life].filter(Boolean).join(" · ")}</div>
        ${short ? `<p class="pbio">${esc(short)}${short !== bio ? ` <a href="#" class="pmore">more</a>` : ""}</p>` : ""}
        <div class="infolinks">${p.imdb ? `<a href="https://www.imdb.com/name/${esc(p.imdb)}/" target="_blank" rel="noopener">IMDb</a> · ` : ""}<a href="https://www.themoviedb.org/person/${p.id}" target="_blank" rel="noopener">TMDB</a></div>
      </div>
    </div>
    <div id="prows">${rows.length ? "" : `<div class="center dim">Nothing to show.</div>`}</div>`;
  const more = app.querySelector<HTMLAnchorElement>(".pmore");
  if (more)
    more.onclick = (e) => {
      e.preventDefault();
      app.querySelector(".pbio")!.textContent = bio;
    };
  const box = app.querySelector("#prows")!;
  box.innerHTML = rows.map(tmdbShelf).join("");
  Array.prototype.forEach.call(box.children, (sec: Element, i: number) => bindShelf(sec, rows[i]));
}

async function renderWatch(hash: string, fileIdx: number) {
  app.innerHTML = `
    <div class="titlebar"><h2 id="title">…</h2><button class="ghost heart" id="heart" hidden title="Add to My favs">♡</button></div>
    <div class="player">
      <video id="v" controls playsinline preload="auto" x-webkit-airplay="allow"></video>
      <div class="overlay" id="ov"><div class="spinner"></div><div id="ovmsg">Connecting…</div></div>
      <div class="nextup" id="nextup" hidden>
        <div class="dim">Next episode</div>
        <div class="nextname" id="nextname"></div>
        <div class="nextbtns"><button id="nextplay">Play now</button><button class="ghost" id="nextcancel">Cancel</button></div>
      </div>
    </div>
    <div class="seek" id="seek" tabindex="0" hidden><div class="track"></div><div class="avail"></div><div class="pos"></div><div class="hover" hidden></div></div>
    <div class="controls">
      <span class="time" id="time"></span>
      <select id="audio" hidden></select>
      <select id="subs" hidden></select>
      <select id="quality" hidden title="Quality"></select>
      <button class="ghost" id="atmosbtn" hidden></button>
      <span class="badge atmos" id="atmosstate" hidden></span>
      <button class="ghost" id="airplay" hidden title="Play on Apple TV">AirPlay</button>
      <span class="dim" id="mode"></span>
    </div>
    <div class="hint">OK: play/pause · ◀ ▶: seek · ⏩ ⏪ · Back</div>
    <div class="info" id="info"></div>
    <div class="stats" id="stats"></div>
    <div class="episodes" id="eps"></div>
    <div class="files list" id="files"></div>
    <div class="similar" id="similar"></div>`;
  const $ = <T extends HTMLElement>(id: string) => app.querySelector<T>("#" + id)!;
  const v = $<HTMLVideoElement>("v");
  const ov = $("ov");
  const ovmsg = $("ovmsg");
  const seek = $("seek");
  const timeEl = $("time");
  const audioSel = $<HTMLSelectElement>("audio");
  const subsSel = $<HTMLSelectElement>("subs");
  let alive = true;
  let status: any = null;
  let probe: any = null;
  let offset = 0; // start time of the current transcoded stream
  let audioIdx = -1;
  let titleKey = `hash:${hash}`;
  const timers: Timer[] = [];
  cleanup = () => {
    alive = false;
    timers.forEach(clearInterval);
    savePos();
    v.removeAttribute("src");
    v.load();
  };

  const cur = () => (probe?.mode === "direct" || probe?.mode === "native" ? v.currentTime : offset + v.currentTime);
  const savePos = () => {
    if (!probe || cur() < 5) return;
    // text/plain: Chrome 59-80 (LG webOS = 79, Tizen) throws on a beacon Blob whose type isn't CORS-safelisted
    // (crbug 490015) - positions were never saved there. The server parses the body as JSON either way.
    const body = JSON.stringify({ hash, file: fileIdx, t: cur() });
    try {
      if (navigator.sendBeacon && navigator.sendBeacon("/api/position", new Blob([body], { type: "text/plain;charset=UTF-8" }))) return;
    } catch {}
    fetch("/api/position", { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true } as RequestInit).catch(() => {});
  };

  const overlay = (msg: string | null) => {
    ov.hidden = msg === null;
    if (msg !== null) ovmsg.innerHTML = msg;
  };

  const drawStatus = () => {
    if (!status) return;
    const f = status.files.find((x: any) => x.index === fileIdx);
    $("title").textContent = playingTitle(status.title, f?.path);
    $("stats").innerHTML = [
      `file <b>${Math.round((f?.done ?? 0) * 100)}%</b>`,
      `↓ <b>${fmtSpeed(status.dlspeed)}</b>`,
      `↑ <b>${fmtSpeed(status.upspeed)}</b>`,
      `peers <b>${status.seeds}</b>`,
      `ratio <b>${status.ratio.toFixed(2)}</b>`,
    ]
      .map((x) => `<span>${x}</span>`) // one flex item each (bare text + <b> lost the space between them)
      .join("");
    const avail = seek.querySelector<HTMLElement>(".avail")!;
    avail.style.width = `${(f?.contiguous ?? 0) * 100}%`;
    const vids = status.files.filter((x: any) => x.video || x.index === fileIdx);
    $("files").innerHTML =
      vids.length > 1 && !(eps && eps.episodes.length)
        ? vids
            .sort((a: any, b: any) => a.path.localeCompare(b.path))
            .map(
              (x: any) => `
        <div class="row ${x.index === fileIdx ? "current" : ""}">
          <a class="name" href="/watch/${hash}/${x.index}" data-link>${esc(x.path.split("/").pop())}</a>
          <div class="meta">${fmtSize(x.size)}</div>
          <div class="bar"><div style="width:${x.done * 100}%"></div></div>
        </div>`,
            )
            .join("")
        : "";
    if (!probe) {
      overlay(
        status.seeds === 0 && status.dlspeed === 0
          ? `Finding sources…`
          : `Buffering… ${fmtSpeed(status.dlspeed)}<br><span class="dim">${status.seeds} sources</span>`,
      );
    }
  };

  // ---- series: episode list, prefetch + autoplay of the next episode
  let eps: any = null;
  let prefetched = false;
  const epTitle = (e: any) => `S${String(e.season).padStart(2, "0")}E${String(e.episode).padStart(2, "0")}`;
  const openEpisode = async (e: any, prefetch = false): Promise<{ hash: string; file: number } | null> => {
    if (e.target) return e.target;
    const r = await api<{ hash: string; file: number }>("/api/quickplay", {
      method: "POST",
      body: JSON.stringify({ type: "tv", tmdbId: eps.show.tmdbId, season: e.season, episode: e.episode, prefetch, caps: CAPS, alang: audioPrefs()[`tv:${eps.show.tmdbId}`] }),
    });
    e.target = r;
    return r;
  };
  const playEpisode = async (e: any, el?: HTMLElement) => {
    if (el) el.classList.add("loading");
    try {
      const t = await openEpisode(e);
      if (t && alive) go(`/watch/${t.hash}/${t.file}`);
    } catch (err: any) {
      if (el) {
        el.classList.remove("loading");
        el.querySelector(".epmeta")!.textContent = err.message || "Not available";
      }
    }
  };
  const drawEpisodes = () => {
    const box = $("eps");
    if (!eps || !eps.episodes.length) {
      box.innerHTML = "";
      return;
    }
    const seasonSel =
      eps.seasons.length > 1
        ? `<select id="season">${eps.seasons
            .map((x: any) => `<option value="${x.season}" ${x.season === eps.season ? "selected" : ""}>Season ${x.season}</option>`)
            .join("")}</select>`
        : `<span class="dim">Season ${eps.season}</span>`;
    box.innerHTML = `
      <div class="ephead"><h3>${esc(eps.show.title)}</h3>${seasonSel}</div>
      <div class="list">${eps.episodes
        .map((e: any, i: number) => {
          const frac = e.completed ? 1 : e.position && e.runtime ? Math.min(1, e.position / (e.runtime * 60)) : 0;
          return `
        <div class="row ep ${e.current ? "current" : ""}" role="button" tabindex="0" data-i="${i}">
          <div class="still">${e.still ? `<img src="${esc(e.still)}" alt="" loading="lazy">` : ""}${
            frac > 0 ? `<div class="epbar"><div style="width:${frac * 100}%"></div></div>` : ""
          }</div>
          <div class="name">
            <div><b>${e.episode}.</b> ${esc(e.name)}${e.completed ? ` <span class="badge">✓</span>` : ""}${
              e.current ? ` <span class="badge">playing</span>` : e.target || e.local ? ` <span class="badge">downloaded</span>` : ""
            }</div>
            <div class="epmeta dim">${e.runtime ? `${e.runtime} min` : ""}${e.airDate ? ` · ${esc(e.airDate)}` : ""}</div>
            <div class="epov dim">${esc(e.overview)}</div>
          </div>
        </div>`;
        })
        .join("")}</div>`;
    // scroll the playing episode into view inside the list box (not the page)
    const listBox = box.querySelector<HTMLElement>(".list");
    const curRow = box.querySelector<HTMLElement>(".row.ep.current");
    if (listBox && curRow) listBox.scrollTop = Math.max(0, curRow.offsetTop - listBox.offsetTop - 8);
    const sel = box.querySelector<HTMLSelectElement>("#season");
    if (sel) sel.onchange = () => loadEpisodes(Number(sel.value));
    box.querySelectorAll<HTMLElement>(".row.ep").forEach((el) => {
      el.onclick = () => {
        const e = eps.episodes[Number(el.dataset.i)];
        if (!e.current) playEpisode(e, el);
      };
      el.onkeydown = (ev) => {
        if (ev.key === "Enter") (ev.preventDefault(), el.click());
      };
    });
  };
  const loadEpisodes = async (season?: number) => {
    try {
      const r = await api(`/api/episodes/${hash}/${fileIdx}?caps=${CAPS}${season ? `&season=${season}` : ""}`);
      if (!alive || !r.episodes) return;
      const keepNext = eps?.next;
      eps = r;
      if (keepNext?.target && !eps.next?.target && eps.next) eps.next.target = keepNext.target;
      drawEpisodes();
      drawStatus();
    } catch {}
  };
  loadEpisodes();

  // ♥ = My favs (feeds "You might like"; a hearted show gets all its seasons downloaded)
  const heartBtn = $<HTMLButtonElement>("heart");
  let isTv = false;
  const drawRating = (r: number) => {
    heartBtn.classList.toggle("on", r > 0);
    heartBtn.textContent = r > 0 ? "♥" : "♡";
    heartBtn.title = r > 0 ? "Remove from My favs" : isTv ? "Add to My favs - all seasons get downloaded" : "Add to My favs";
  };
  const ratingP = api<{ key: string | null; rating: number }>(`/api/rating/${hash}`);
  ratingP
    .then((r) => {
      if (!alive || !r.key) return;
      isTv = r.key.startsWith("tv:");
      heartBtn.hidden = false;
      let cur = r.rating > 0 ? 1 : 0;
      drawRating(cur);
      const set = async () => {
        const next = cur > 0 ? 0 : 1;
        drawRating(next);
        try {
          await api("/api/rating", { method: "POST", body: JSON.stringify({ hash, rating: next }) });
          cur = next;
          sugCache = null; // front page rows depend on it
        } catch {
          drawRating(cur);
        }
      };
      heartBtn.onclick = () => set();
    })
    .catch(() => {});

  // about the movie / show (+ this episode) - TMDB, loads on its own
  api<any>(`/api/info/${hash}/${fileIdx}`)
    .then((i) => {
      if (alive && i?.title) $("info").innerHTML = infoHtml(i);
    })
    .catch(() => {});

  // "You might also like": similar titles (AI, cached per title on the server) - loads on its own
  api<{ items: any[] }>(`/api/similar/${hash}`)
    .then((r) => {
      if (!alive || !r.items?.length) return;
      const row = { title: "You might also like", items: r.items };
      const box = $("similar");
      box.innerHTML = tmdbShelf(row);
      const section = box.firstElementChild;
      if (section) bindShelf(section, row);
    })
    .catch(() => {});

  const poll = async () => {
    try {
      status = await api(`/api/torrent/${hash}`);
      if (alive) drawStatus();
    } catch {}
  };
  await poll();
  timers.push(setInterval(poll, 2000));
  timers.push(setInterval(savePos, 10000));

  try {
    // the audio language picked earlier for this title (waits briefly for the TMDB match, else per torrent)
    titleKey = (await Promise.race([ratingP.then((r) => r.key).catch(() => null), new Promise<null>((r) => setTimeout(() => r(null), 2500))])) ?? `hash:${hash}`;
    const alang = audioPrefs()[titleKey];
    probe = await api(`/api/play/${hash}/${fileIdx}?caps=${CAPS}${alang ? `&alang=${encodeURIComponent(alang)}` : ""}`);
    if (probe.langMatched) audioIdx = probe.defaultAudio;
  } catch (e: any) {
    overlay(`Can't play this file: ${esc(e.message)}`);
    return;
  }
  if (!alive) return;

  const dur = probe.duration;
  $("mode").textContent = probe.mode === "direct" ? "" : probe.mode === "remux" ? "HD" : "converted";
  if (resumeOverride && resumeOverride.hash === hash && resumeOverride.file === fileIdx) {
    probe.position = resumeOverride.t;
    resumeOverride = null;
  }

  // ---- quality: Auto (screen + connection) / Original / fixed level
  const qSel = $<HTMLSelectElement>("quality");
  const ladder: { h: number }[] = probe.ladder ?? [];
  const pref = localStorage.getItem(QKEY) || "auto";
  let level = 0; // 0 = original
  if (pref === "auto") level = NATIVE_HLS ? 0 : await autoLevel(probe);
  else if (pref !== "orig" && ladder.some((l) => l.h === Number(pref))) level = Number(pref);
  if (!alive) return;
  const srcH: number = probe.video?.height ?? 0;
  const srcLabel = srcH ? `Original (${srcH}p)` : "Original";
  // offered levels: lower ones (another release, else transcode) + higher ones (only via another release)
  const levels = [1080, 720, 480].filter((h) => !srcH || Math.abs(h - srcH) > 40);
  if (levels.length) {
    qSel.hidden = false;
    qSel.innerHTML =
      `<option value="auto">Auto${pref === "auto" && !NATIVE_HLS ? ` (${level ? level + "p" : "original"})` : ""}</option>` +
      `<option value="orig">${srcLabel}</option>` +
      levels.map((h) => `<option value="${h}">${h}p</option>`).join("");
    const shown = pref === "auto" || pref === "orig" ? pref : level ? String(level) : "orig";
    qSel.value = shown;
    qSel.onchange = async () => {
      const val = qSel.value;
      const reopen = (at: string, h: string, f: number) => {
        localStorage.setItem(QKEY, val);
        resumeOverride = { hash: at, file: f, t: cur() };
        if (at === hash && f === fileIdx) route();
        else go(`/watch/${at}/${f}`);
        void h;
      };
      if (val === "auto" || val === "orig") return reopen(hash, val, fileIdx);
      const h = Number(val);
      // a real release in that quality beats transcoding: look for one first
      qSel.disabled = true;
      const modeEl = $("mode");
      const prevMode = modeEl.textContent;
      modeEl.textContent = `Looking for a ${h}p release…`;
      try {
        const r = await api<{ hash?: string; file?: number }>("/api/alternate", {
          method: "POST",
          body: JSON.stringify({ hash, file: fileIdx, height: h, caps: CAPS, alang: audioPrefs()[titleKey] }),
        });
        if (!alive) return;
        if (r.hash) return reopen(r.hash, val, r.file ?? 0);
      } catch {}
      if (!alive) return;
      qSel.disabled = false;
      if (srcH && h > srcH) {
        // can't make it sharper by transcoding
        modeEl.textContent = `No ${h}p release found`;
        qSel.value = shown;
        setTimeout(() => alive && (modeEl.textContent = prevMode), 4000);
        return;
      }
      reopen(hash, val, fileIdx); // none found: transcode this one down
    };
  }
  // a lower level always goes through the converting pipe (custom seek bar etc.)
  if (level && !NATIVE_HLS) {
    probe.mode = "transcode";
    $("mode").textContent = `${level}p`;
  }

  // Safari/iOS: use the server's HLS (whole timeline scrubbable, AirPlay-able, no custom seek bar)
  let appleUrl = "";
  const loadApple = async () => {
    // auto -> adaptive (all levels), orig -> original only, else a fixed level
    const aq = pref === "auto" ? 0 : pref === "orig" ? -1 : level || -1;
    const r = await api<{ kind: string; url: string; mode?: string }>(
      `/api/apple/${hash}/${fileIdx}?a=${audioIdx}&q=${aq}&caps=${CAPS}`,
    );
    appleUrl = r.url;
    $("mode").textContent = r.kind === "file" ? "" : pref === "auto" ? "Auto" : r.mode === "copy" ? "HD" : level ? `${level}p` : "converted";
  };
  if (NATIVE_HLS && (probe.mode !== "direct" || level)) {
    try {
      await loadApple();
      probe.mode = "native";
    } catch (e: any) {
      console.warn("apple hls unavailable, falling back", e);
    }
  }
  if (!alive) return;

  // AirPlay (Safari): Apple TV fetches the (token-signed) URL itself
  const airBtn = $<HTMLButtonElement>("airplay");
  const anyV = v as any;
  if ((window as any).WebKitPlaybackTargetAvailabilityEvent) {
    // Always shown on Safari: iOS doesn't reliably tell the page that an Apple TV is reachable (the button stayed
    // hidden while fullscreen offered AirPlay). Apple's picker finds the devices itself; "not available" only dims it.
    airBtn.hidden = false;
    v.addEventListener("webkitplaybacktargetavailabilitychanged", (e: any) => {
      airBtn.classList.toggle("dimmed", e.availability !== "available");
    });
    airBtn.onclick = () => anyV.webkitShowPlaybackTargetPicker?.();
    v.addEventListener("webkitcurrentplaybacktargetiswirelesschanged", () => {
      const wireless = !!anyV.webkitCurrentPlaybackTargetIsWireless;
      airBtn.textContent = wireless ? "AirPlay ✓" : "AirPlay";
      if (wireless) overlay(null);
    });
  }

  // audio tracks + "Find English dub…" (another release of this episode/movie that has it) when none is in the file
  {
    const switchable = probe.audio.length > 1 && probe.mode !== "direct";
    const wantLang = audioPrefs()[titleKey] || "en";
    const canFind = /^(tv|movie):/.test(titleKey) && !probe.audio.some((a: any) => trackLang(a) === wantLang);
    if (switchable || canFind) {
      audioSel.hidden = false;
      const sel = probe.defaultAudio ?? -1;
      audioSel.innerHTML =
        probe.audio
          .map(
            (a: any) =>
              `<option value="${a.index}" ${a.index === sel || (probe.defaultAudio === undefined && a.default) ? "selected" : ""}>${esc(audioLabel(a))}</option>`,
          )
          .join("") + (canFind ? `<option value="find">Find ${esc(langName(wantLang) ?? wantLang)} dub…</option>` : "");
      if (switchable) audioIdx = Number(audioSel.value);
      audioSel.title = "Audio (language / dub)";
      const shown = audioSel.value;
      audioSel.onchange = async () => {
        if (audioSel.value === "find") {
          audioSel.disabled = true;
          const modeEl = $("mode");
          const prevMode = modeEl.textContent;
          modeEl.textContent = `Looking for a ${langName(wantLang)} dub…`;
          try {
            const r = await api<{ hash?: string; file?: number }>("/api/alternate", {
              method: "POST",
              body: JSON.stringify({ hash, file: fileIdx, height: 0, alang: wantLang, caps: CAPS }),
            });
            if (!alive) return;
            if (r.hash) {
              setAudioPref(titleKey, wantLang); // next episodes: look for the dub too
              resumeOverride = { hash: r.hash, file: r.file ?? 0, t: cur() };
              go(`/watch/${r.hash}/${r.file ?? 0}`);
              return;
            }
            modeEl.textContent = `No ${langName(wantLang)} dub found`;
          } catch (e: any) {
            modeEl.textContent = e?.message || "Search failed";
          }
          audioSel.disabled = false;
          audioSel.value = shown;
          setTimeout(() => alive && (modeEl.textContent = prevMode), 5000);
          return;
        }
        if (!switchable) return;
        audioIdx = Number(audioSel.value);
        const l = trackLang(probe.audio.find((a: any) => a.index === audioIdx) ?? {});
        // remembered for this title: next episodes start in this language
        if (l) ratingP.then((r) => r.key ?? titleKey, () => titleKey).then((k) => setAudioPref(k, l));
        if (probe.mode === "native") {
          const t = cur();
          await loadApple();
          v.src = appleUrl;
          startAt(t);
          return;
        }
        startAt(cur());
      };
    }
  }

  // ---- Dolby Atmos: per-device preference + whether the playing track keeps it
  {
    const atmosTracks = probe.audio.filter((a: any) => a.atmos);
    const btn = $<HTMLButtonElement>("atmosbtn");
    const state = $("atmosstate");
    if (atmosTracks.length) {
      const eac3 = canEac3() || NATIVE_HLS;
      btn.hidden = false;
      btn.textContent = prefersAtmos() ? "Atmos: on" : "Atmos: off";
      btn.title = eac3
        ? "Prefer Dolby Atmos on this device (picks Atmos releases and tracks)"
        : "This browser can't play Dolby Digital Plus - Atmos needs Apple TV / Safari or a TV with Dolby";
      btn.disabled = !eac3;
      btn.onclick = () => {
        localStorage.setItem(ATMOS_KEY, prefersAtmos() ? "0" : "1");
        CAPS = mediaCaps();
        resumeOverride = { hash, file: fileIdx, t: cur() };
        route();
      };
      // is Atmos actually kept for the track that plays?
      const idx = audioIdx >= 0 ? audioIdx : (probe.defaultAudio ?? -1);
      const tr = probe.audio.find((a: any) => a.index === idx) ?? probe.audio[0];
      const kept = tr?.atmos && tr.codec === "eac3" && eac3 && (NATIVE_HLS || CAPS.split(",").includes("eac3"));
      state.hidden = !tr?.atmos;
      state.textContent = kept ? "Atmos ✓" : tr?.codec === "truehd" ? "Atmos lost (TrueHD)" : "Atmos → stereo here";
      state.title = kept
        ? "The Dolby Atmos track is passed through untouched"
        : "This track is converted on this device - Atmos needs Apple TV / Safari or a TV with Dolby (and a DD+ Atmos release)";
    }
  }

  // subtitles: external files in the torrent + embedded text tracks
  const subOpts: { label: string; url: string }[] = [
    ...probe.externalSubs.map((s: any) => ({ label: s.name, url: `/media/${hash}/${s.index}/extsub` })),
    ...probe.subs
      .filter((s: any) => s.text)
      .map((s: any) => ({
        label: [s.lang, s.title].filter(Boolean).join(" · ") || `Track ${s.index + 1}`,
        url: `/media/${hash}/${fileIdx}/sub/${s.index}`,
      })),
  ];
  if (subOpts.length && probe.mode !== "native") {
    subsSel.hidden = false;
    subsSel.innerHTML =
      `<option value="">No subtitles</option>` + subOpts.map((s, i) => `<option value="${i}">${esc(s.label)}</option>`).join("");
    subsSel.onchange = () => {
      v.querySelectorAll("track").forEach((t) => t.remove());
      const s = subOpts[Number(subsSel.value)];
      if (subsSel.value === "" || !s) return;
      const tr = document.createElement("track");
      tr.kind = "subtitles";
      tr.src = s.url;
      tr.default = true;
      tr.label = s.label;
      v.appendChild(tr);
      // cues are relative to file start; shift them for transcoded streams
      tr.addEventListener("load", () => shiftCues());
      tr.track.mode = "showing";
    };
  }
  let shifted = 0;
  const shiftCues = () => {
    if (probe.mode === "direct" || probe.mode === "native") return;
    const t = v.textTracks[0];
    if (!t?.cues) return;
    const delta = shifted - offset;
    for (const c of Array.from(t.cues)) {
      c.startTime += delta;
      c.endTime += delta;
    }
    shifted = offset;
  };

  let startSeq = 0;
  const startAt = async (t: number) => {
    t = Math.max(0, Math.min(t, dur ? dur - 1 : t));
    const seq = ++startSeq;
    overlay(`Buffering…`);
    if (probe.mode === "native") {
      if (v.getAttribute("src") !== appleUrl) v.src = appleUrl;
      if (v.readyState >= 1) v.currentTime = t;
      else v.addEventListener("loadedmetadata", () => (v.currentTime = t), { once: true });
    } else if (probe.mode === "direct") {
      if (!v.src) v.src = `/media/${hash}/${fileIdx}/raw`;
      v.currentTime = t;
    } else {
      // copy-mode streams can only start on a keyframe: ask where, so clock + subtitles line up exactly
      if (probe.mode === "remux" && t > 0) {
        try {
          const r = await api<{ t: number }>(`/api/seekpoint/${hash}/${fileIdx}?t=${t.toFixed(3)}&caps=${CAPS}`);
          if (seq !== startSeq || !alive) return; // superseded by a newer seek
          t = r.t;
        } catch {}
      }
      offset = t;
      v.src = `/media/${hash}/${fileIdx}/mp4?t=${t.toFixed(3)}&a=${audioIdx}&caps=${CAPS}${level ? `&q=${level}` : ""}`;
      shiftCues();
    }
    v.play().catch(() => overlay(null));
  };

  v.addEventListener("waiting", () => overlay("Buffering…"));
  v.addEventListener("playing", () => overlay(null));
  v.addEventListener("canplay", () => overlay(null));
  v.addEventListener("error", () => {
    if (alive && v.getAttribute("src")) overlay(`Playback error — <a href="#" onclick="location.reload()">retry</a>`);
  });

  // custom seek bar for converted streams (browser can't seek in a live mp4 pipe)
  if (probe.mode !== "direct" && probe.mode !== "native") {
    seek.hidden = false;
    const pos = seek.querySelector<HTMLElement>(".pos")!;
    const hover = seek.querySelector<HTMLElement>(".hover")!;
    const frac = (e: MouseEvent) => {
      const r = seek.getBoundingClientRect();
      return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    };
    seek.onmousemove = (e) => {
      hover.hidden = false;
      hover.style.left = `${frac(e) * 100}%`;
      hover.textContent = fmtTime(frac(e) * dur);
    };
    seek.onmouseleave = () => (hover.hidden = true);
    seek.onclick = (e) => startAt(frac(e) * dur);
    v.addEventListener("timeupdate", () => {
      pos.style.width = `${dur ? (cur() / dur) * 100 : 0}%`;
    });
  }

  // remote / keyboard control. Seeks on converted streams restart ffmpeg, so coalesce key repeats.
  let seekTarget: number | null = null;
  let seekTimer: Timer | null = null;
  setPlayerKeys({
    video: v,
    seekBar: seek,
    toggle: () => (v.paused ? v.play().catch(() => {}) : v.pause()),
    seekBy: (sec) => {
      if (probe.mode === "direct" || probe.mode === "native") {
        v.currentTime = Math.max(0, Math.min(dur || Infinity, v.currentTime + sec));
        return;
      }
      seekTarget = Math.max(0, Math.min(dur - 1, (seekTarget ?? cur()) + sec));
      timeEl.textContent = `→ ${fmtTime(seekTarget)} / ${fmtTime(dur)}`;
      seek.querySelector<HTMLElement>(".pos")!.style.width = `${(seekTarget / dur) * 100}%`;
      if (seekTimer) clearTimeout(seekTimer);
      seekTimer = setTimeout(() => {
        const t = seekTarget!;
        seekTarget = null;
        startAt(t);
      }, 700);
    },
  });
  {
    const prev = cleanup;
    cleanup = () => {
      setPlayerKeys(null);
      if (seekTimer) clearTimeout(seekTimer);
      prev?.();
    };
  }
  v.tabIndex = 0;
  if (isTV) v.focus();
  v.addEventListener("timeupdate", () => {
    timeEl.textContent = `${fmtTime(cur())} / ${fmtTime(dur)}`;
  });
  // at 85%: get the next episode downloading so it's ready when this one ends
  v.addEventListener("timeupdate", () => {
    if (prefetched || !dur || !eps?.next || cur() / dur < 0.85) return;
    prefetched = true;
    const n = eps.next;
    if (n.target) {
      api("/api/prefetch", { method: "POST", body: JSON.stringify(n.target) }).catch(() => {});
    } else {
      openEpisode(n, true).catch(() => (prefetched = false));
    }
  });

  let nextTimer: Timer | null = null;
  const nextUp = $("nextup");
  const hideNext = () => {
    if (nextTimer) clearInterval(nextTimer);
    nextTimer = null;
    nextUp.hidden = true;
  };
  $("nextcancel").onclick = hideNext;
  $("nextplay").onclick = () => {
    hideNext();
    if (eps?.next) playEpisode(eps.next);
  };
  v.addEventListener("ended", () => {
    api("/api/position", { method: "POST", body: JSON.stringify({ hash, file: fileIdx, t: 0, completed: true }) }).catch(() => {});
    if (!eps?.next || !alive) return;
    let left = 10;
    const name = `${epTitle(eps.next)}${eps.next.name ? " · " + eps.next.name : ""}`;
    const tick = () => ($("nextname").textContent = `${name} — in ${left} s`);
    tick();
    nextUp.hidden = false;
    $("nextplay").focus();
    nextTimer = setInterval(() => {
      left--;
      if (left <= 0) {
        hideNext();
        playEpisode(eps.next);
      } else tick();
    }, 1000);
  });
  {
    const prev = cleanup;
    cleanup = () => {
      hideNext();
      prev?.();
    };
  }

  const resume = probe.position > 30 && probe.position < dur - 60 ? probe.position : 0;
  startAt(resume);
}

// ---------------- boot ----------------
installKeys(() => {
  if (location.pathname !== "/") history.back();
});
(async () => {
  const me = await refreshAccount();
  if (!me.authed) renderLogin();
  else route();
})();
